import { mapPhaseLabel, mapProgressMessage, mapSimulationError, normalizeRunStatus } from "./simulationProgress";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { quickStep, withoutQuick } from "../../components/quickStudyRun";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Grow,
  LinearProgress,
  MenuItem,
  Paper,
  Popper,
  Stack,
  SvgIcon,
  Skeleton,
  TextField,
  Tooltip,
  IconButton,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AssumptionCheck from "../../components/AssumptionCheck";
import FeasibilityShell from "./FeasibilityShell";
import ManualLink from "../../components/ManualLink";
import CommercialOfferCard from "./CommercialOfferCard";
import StudyResultsDialog from "./StudyResultsDialog";
import { preRunBlockers, preRunWarnings, warningsKey } from "./preRunChecks";
import { indicativePricesInUse } from "../../components/config/studyPresets";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import BatteryComparisonCard from "./BatteryComparisonCard";
import { studyBessBlock } from "../../components/config/batteryComparison";
import { COMMERCIAL_OFFER_SECTION_ID, commercialOfferValidation, normalizeCommercialOffer, commercialProjectOfferMode, isMerchantCommercialMode } from "../../components/config/commercialOffer";
import { getReportLanguage } from "../../utils/reportLanguage";
import { useFile } from "../../context/FileContext";
import { useLanguage } from "../../context/LanguageContext";
import { translateLegacyText } from "../../i18n/legacyTranslation";
import WeatherSourceBadge from "../../components/weather/WeatherSourceBadge";
import PvFinancialRisk from "../../components/PvFinancialRisk";
import { useHistory } from "../../context/HistoryContext";
import { useAuth } from "../../context/AuthContext";
import { useDemo } from "../../context/DemoContext";
import { metenApi } from "../../services/metenApi";
import { fetchDamPriceCoverage, requestFeasibilityStreamTicket } from "../../services/api";
import { DEFAULT_CONFIG } from "../../components/config/defaultConfig";
import { prepareProjectConfigForPersistence } from "../../components/config/pvLosses";
import { sceneShadingPending, sceneSimulationIssues } from "../../components/config/sceneSimulationIssues";
import { prepareSceneSimulation } from "../../utils/prepareSceneSimulation";
import { isSceneCalculationCancelled, prepareSceneInBackground } from "../../services/sceneCalculation";
import { scenePreparationMessage } from "../../utils/scenePreparationIssue";
import { configWithoutLayoutShading } from "../../utils/runWithoutLayoutShading";
import { getModuleDetails } from "../../services/api";
import { orientationsFromConfig } from "../../components/config/orientations";
import { saveOrientationShading } from "../../components/config/orientationEvidence";
import { deriveInstalledBessPowerMw } from "../../components/config/tollingFixedCapacity";
import {
  buildBalancingV1Payload,
  buildIncludedMarketsList,
  includedMarketsToMap,
  validateBalancingV1Config,
} from "../../components/config/balancingV1";
import { buildCapacityPaymentsPayload } from "../../components/config/capacityPayments";
import { buildGuaranteesOfOriginPayload } from "../../components/config/guaranteesOfOrigin";
import { buildPpaPayload } from "../../components/config/ppa";
import {
  alignBtmSwitches,
  btmValuationLabels,
  buildBtmPayload,
  isBtmSettlement,
  isStandaloneBessSystem,
  normalizeBtm,
  validateBtm,
} from "../../components/config/btm";
import { isVirtualNetBilling } from "../../components/config/virtualNetBilling";
import {
  buildDemandProjectionPayload,
  normalizeDemandProjection,
  validateDemandProjection,
} from "../../components/config/demandProjection";
import { formatApiError } from "../../utils/apiErrors";
import {
  defaultSimulationYear,
  projectLifetimeDates,
  operatingStartYear,
  validOperatingStartYear,
  simulationYears,
} from "../../utils/simulationYears";
import {
  getDemoActionDisabledMessage,
  getDemoExportDisabledMessage,
  getRevenueModelDisabledMessage,
} from "../../utils/demoAccess";

// Must match the flag in General.jsx. Default false when env var is absent.
import { normalizeMarketZone, supportsIdaMarket } from "../../utils/marketSupport";
import { deriveProjectAssets, assetTypeDefinition, assetTypeIssue, studyAssetIssue } from "../../utils/projectAssets";
import { assetSummary } from "../../utils/portfolio";
import { describe as describeDegradation } from "../../utils/degradationCurve";

// The revenue streams a study can include, in the order the market stack page
// lists them. Named here so the run scope can state what will actually be
// priced: the stack lives two pages away and changes the result more than
// anything on this one.
// Run-scope card figures. A 10.62 kWp roof read "11 kWp" and a 15.36 kWh
// battery "0.02 MW / 0.02 MWh": small systems keep two decimals in kW / kWh,
// large ones switch to MW / MWh as before.
function formatScopeKw(kw) {
  const value = Number(kw) || 0;
  if (Math.abs(value) < 100) return String(Math.round(value * 100) / 100);
  return Math.round(value).toLocaleString("en-US");
}

function formatScopePower(k, unit) {
  const value = Number(k) || 0;
  if (Math.abs(value) >= 1000) return `${Math.round(value / 10) / 100} M${unit}`;
  return `${formatScopeKw(value)} k${unit}`;
}

const MARKET_LABELS = [
  ["dam", "Day-ahead"],
  ["ida1", "Intraday 1"],
  ["ida2", "Intraday 2"],
  ["ida3", "Intraday 3"],
  ["balancing", "Balancing"],
  ["ancillary", "Ancillary services"],
  ["capacity", "Capacity market"],
  ["go", "Guarantees of origin"],
  ["other", "Other"],
];

const IDA_BANKABILITY_ENABLED = process.env.REACT_APP_IDA_BANKABILITY_ENABLED === "true";
// A warning, no longer a lock. Every output below is an artefact of the run it
// came from: the proposal PDF and both teasers are stored files written by the
// worker, and the XLSX endpoint ignores the body it is sent and reads the
// config stored with the run. None of them can describe the current settings,
// so none of them can be made inconsistent by a change to those settings.
// Blocking them only forced a re-run to fetch files that already existed --
// and did so per browser, since the "matching run" memory is localStorage.
const STALE_CONFIG_WARNING =
  "The settings have changed since the last simulation. These outputs describe that last completed run, not the current settings. Run the study again for outputs that match them.";
const COMPLETED_RUN_STORAGE_PREFIX = "feasibility.completedRunConfig";
//: How long the Cancel button may stay in its "cancelling" state without the
//: run reaching a terminal one. Cancellation is cooperative and the worker can
//: be inside a phase that does not look at the flag, so a wait is expected --
//: but an unbounded one is a stuck control, not a slow one.
const CANCEL_ACK_TIMEOUT_MS = 30000;

// Resolve per-model includedRevenueStreams based on the active settlementType.
// NOTE: Behind-the-Meter ("BTM") deliberately has no entry in these resolvers.
// A behind-the-meter system is settled against the site's retail tariff and
// earns no wholesale, capacity or balancing revenue, so returning null (and
// therefore shipping no market stack) is the correct behaviour, not an omission.
// The backend rejects IDA/balancing outright for this revenue model.
function getActiveIncludedRevenueStreams(config) {
  const st = config?.settlementType;
  if (st === "TollingFixed") return config?.tollingFixedCapacity?.includedRevenueStreams;
  if (st === "TollingFloorUpside") return config?.tollingFloorUpside?.includedRevenueStreams;
  if (st === "HybridPartial") return config?.hybridPartialTolling?.includedRevenueStreams;
  if (st === "Arbitrage") return config?.arbitrageMerchant?.includedRevenueStreams;
  if (st === "PPA") return config?.ppa?.includedRevenueStreams;
  return null;
}

function getActiveBalancingV1Config(config) {
  const st = config?.settlementType;
  if (st === "TollingFixed") return config?.tollingFixedCapacity?.balancingV1;
  if (st === "TollingFloorUpside") return config?.tollingFloorUpside?.balancingV1;
  if (st === "HybridPartial") return config?.hybridPartialTolling?.balancingV1;
  if (st === "Arbitrage") return config?.arbitrageMerchant?.balancingV1;
  if (st === "PPA") return config?.ppa?.balancingV1;
  return null;
}

function getActiveCapacityPaymentsConfig(config) {
  const st = config?.settlementType;
  if (st === "TollingFixed") return config?.tollingFixedCapacity?.capacityPayments;
  if (st === "TollingFloorUpside") return config?.tollingFloorUpside?.capacityPayments;
  if (st === "HybridPartial") return config?.hybridPartialTolling?.capacityPayments;
  if (st === "Arbitrage") return config?.arbitrageMerchant?.capacityPayments;
  if (st === "PPA") return config?.ppa?.capacityPayments;
  return null;
}

function getActiveGuaranteesOfOriginConfig(config) {
  const st = config?.settlementType;
  if (st === "TollingFixed") return config?.tollingFixedCapacity?.guaranteesOfOrigin;
  if (st === "TollingFloorUpside") return config?.tollingFloorUpside?.guaranteesOfOrigin;
  if (st === "HybridPartial") return config?.hybridPartialTolling?.guaranteesOfOrigin;
  if (st === "Arbitrage") return config?.arbitrageMerchant?.guaranteesOfOrigin;
  if (st === "PPA") return config?.ppa?.guaranteesOfOrigin;
  return null;
}

function setActiveIncludedRevenueStreams(config, includedRevenueStreams) {
  const st = config?.settlementType;
  if (st === "TollingFixed") {
    return {
      ...config,
      tollingFixedCapacity: {
        ...(config?.tollingFixedCapacity || {}),
        includedRevenueStreams,
      },
    };
  }
  if (st === "TollingFloorUpside") {
    return {
      ...config,
      tollingFloorUpside: {
        ...(config?.tollingFloorUpside || {}),
        includedRevenueStreams,
      },
    };
  }
  if (st === "HybridPartial") {
    return {
      ...config,
      hybridPartialTolling: {
        ...(config?.hybridPartialTolling || {}),
        includedRevenueStreams,
      },
    };
  }
  if (st === "Arbitrage") {
    return {
      ...config,
      arbitrageMerchant: {
        ...(config?.arbitrageMerchant || {}),
        includedRevenueStreams,
      },
    };
  }
  if (st === "PPA") {
    return {
      ...config,
      ppa: {
        ...(config?.ppa || {}),
        includedRevenueStreams,
      },
    };
  }
  return config;
}

function isLeapYear(year) {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

// Simulation horizon is derived internally from the simulation year:
// full-year runs only (366 days for leap years, 365 otherwise).
function deriveHorizonDays(year) {
  return isLeapYear(Number(year)) ? 366 : 365;
}

// Sorted keys with JSON semantics. The fingerprint built from the live config
// is compared against one rebuilt from the config the backend stored for the
// run -- and that copy went through JSON, so a key holding `undefined` is not
// there at all. This used to write such a key as null, which made the two
// sides differ on nothing: the run had completed, its config was unchanged,
// and the report buttons stayed locked until the study was run again. A
// Date, likewise, becomes the string JSON would have written, not `{}`.
function stableNormalize(value) {
  if (value === null || typeof value !== "object") return value;
  if (typeof value.toJSON === "function") return value.toJSON();
  if (Array.isArray(value)) {
    return value.map((item) => (item === undefined ? null : stableNormalize(item)));
  }
  return Object.keys(value)
    .sort()
    .reduce((acc, key) => {
      if (value[key] === undefined) return acc;
      acc[key] = stableNormalize(value[key]);
      return acc;
    }, {});
}

function stableStringify(value) {
  return JSON.stringify(stableNormalize(value));
}

function getComparableIncludedMarkets(config) {
  const markets = config?.marketStack?.includedMarkets || {};
  const streams = getActiveIncludedRevenueStreams(config) || {};
  return includedMarketsToMap(markets, streams);
}

function getComparableIncludedRevenueStreams(config) {
  const streams = getActiveIncludedRevenueStreams(config) || {};
  return {
    dam: Boolean(streams.dam ?? true),
    ida1: Boolean(streams.ida1),
    ida2: Boolean(streams.ida2),
    ida3: Boolean(streams.ida3),
    balancing: Boolean(streams.balancing),
    ancillary: Boolean(streams.ancillary),
    capacity: Boolean(streams.capacity),
    go: Boolean(streams.go),
    other: Boolean(streams.other),
  };
}

function buildRunConfigSignature(config, runWindow = {}) {
  const economics = config?.feasibility?.economics || {};
  return {
    // Opt-in provenance: legacy fingerprints stay identical. Applying a sizing
    // design invalidates a completed study without deleting its saved results.
    ...(config?.pvSizingAppliedDesign ? { pvSizingAppliedDesign: config.pvSizingAppliedDesign } : {}),
    runWindow: {
      start: runWindow.start || null,
      end: runWindow.end || null,
      simulationYear: config?.feasibility?.simulationYear ?? null,
      // Preserve the fingerprint of legacy runs when the effective start is unchanged.
      ...(operatingStartYear(config) !== (Number(config?.feasibility?.simulationYear) || new Date().getFullYear() - 1)
        ? { operatingStartYear: operatingStartYear(config) } : {}),
      simulationHorizonDays: config?.feasibility?.simulationHorizonDays ?? null,
      settlementPeriod: config?.feasibility?.settlementPeriod ?? null,
    },
    pvRisk: config?.feasibility?.pvRisk || null,
    pvUncertainty: config?.pvUncertainty || null,
    revenueModel: config?.settlementType || null,
    biddingZone: config?.biddingZone || null,
    marketStack: {
      includedMarkets: getComparableIncludedMarkets(config),
      balancing: config?.marketStack?.balancing || null,
    },
    includedRevenueStreams: getComparableIncludedRevenueStreams(config),
    // Regulatory framework (11A/11B) participates in the run fingerprint so
    // changing it correctly marks previous results as stale.
    regulatoryFramework: config?.arbitrageMerchant?.regulatoryFramework || null,
    // PV asset status (new / existing sunk) changes CAPEX inside the engine,
    // so it must also invalidate previous results.
    pvAssetStatus: config?.pvAssetStatus || "new",
    // Behind-the-Meter commercial terms, plus the demand profile and retail
    // tariffs the BTM savings are computed against. Unlike every market-priced
    // model, BTM economics depend directly on feasibility.technical, so those
    // inputs have to be part of the fingerprint or editing the site's load or
    // tariffs would leave a stale report looking current.
    // The supplies' files under virtual net billing are fingerprinted by
    // identity, like the metered profile below; a project without supplies
    // keeps exactly the fingerprint it had.
    btm: Array.isArray(config?.btm?.virtualNetBilling?.supplies)
      ? {
          ...config.btm,
          virtualNetBilling: {
            ...config.btm.virtualNetBilling,
            supplies: config.btm.virtualNetBilling.supplies.map(({ loadProfile, ...supply }) => ({
              ...supply,
              loadProfile: loadProfile?.values?.length
                ? {
                    count: loadProfile.values.length,
                    resolution: loadProfile.resolution || null,
                    source: loadProfile.source || null,
                    importedAt: loadProfile.importedAt || null,
                  }
                : null,
            })),
          },
        }
      : config?.btm || null,
    btmInputs:
      config?.settlementType === "BTM"
        ? {
            // The metered profile is up to 35,040 values, so fingerprint its
            // identity rather than its contents — the value list itself is
            // immutable once imported.
            demand: (() => {
              const demand = config?.feasibility?.technical?.demand || {};
              const { loadProfile, ...rest } = demand;
              return {
                ...rest,
                loadProfile: loadProfile?.values?.length
                  ? {
                      count: loadProfile.values.length,
                      resolution: loadProfile.resolution || null,
                      source: loadProfile.source || null,
                      importedAt: loadProfile.importedAt || null,
                      // Switching the load source changes every number in the
                      // run without changing the file, so it has to be here.
                      use: loadProfile.use !== false,
                    }
                  : null,
              };
            })(),
            tariffs: config?.feasibility?.technical?.tariffs || null,
            peakWindow: config?.feasibility?.technical?.peakWindow || null,
            demandProjection: config?.feasibility?.technical?.demandProjection || null,
            useCase: config?.useCase || null,
            allowGridCharging: config?.allowGridCharging ?? null,
            grid: config?.grid || null,
          }
        : null,
    bessSizing: {
      // The study's own battery: batteries selected only to be compared with
      // it do not change the study, so they must not make its reports stale.
      bess: studyBessBlock(config?.bess),
      batCount: config?.batCount || {},
      batteryDetails: config?.batteryDetails || {},
    },
    financial: {
      investmentCosts: economics.investmentCosts || {},
      // An equipment cost override replaces the catalog hardware value in
      // CAPEX and in the % O&M / insurance basis, so it moves the answer and
      // has to invalidate a previous result -- the same reason pvAssetStatus
      // is in this fingerprint. Leaving it out would let a study show numbers
      // priced on equipment costs the user has since changed.
      equipmentCostOverride: economics.equipmentCostOverride || {},
      financialParams: economics.financialParams || {},
      projectDebt: economics.projectDebt || {},
    },
    tolling: {
      tollingFixedFeePerMWYear: config?.tollingFixedFeePerMWYear ?? null,
      tollingContractYears: config?.tollingContractYears ?? null,
      tollingAvailabilityPct: config?.tollingAvailabilityPct ?? null,
      tollingPenaltyAnnual: config?.tollingPenaltyAnnual ?? null,
      tollingRevenueFloor: config?.tollingRevenueFloor ?? null,
      tollingUpsideSharePct: config?.tollingUpsideSharePct ?? null,
      tollingSettlementFrequency: config?.tollingSettlementFrequency ?? null,
      tollingEscalationPct: config?.tollingEscalationPct ?? null,
      tollingCapacityBasis: config?.tollingCapacityBasis ?? null,
      tollingFixedCapacity: config?.tollingFixedCapacity || {},
      tollingFloorUpside: config?.tollingFloorUpside || {},
      hybridPartialTolling: config?.hybridPartialTolling || {},
    },
    ppa: {
      ppa: config?.ppa || {},
      ppaType: config?.ppaType ?? null,
      ppaFixedPrice: config?.ppaFixedPrice ?? null,
      ppaTodBasePrice: config?.ppaTodBasePrice ?? null,
      ppaFirmBlockLimit: config?.ppaFirmBlockLimit ?? null,
      ppaStartHour: config?.ppaStartHour ?? null,
      ppaEndHour: config?.ppaEndHour ?? null,
    },
  };
}

export function buildRunConfigFingerprint(config, runWindow) {
  return stableStringify(buildRunConfigSignature(config, runWindow));
}

function completedRunStorageKey(projectId) {
  return `${COMPLETED_RUN_STORAGE_PREFIX}.${projectId}`;
}

function readCompletedRunFingerprint(projectId) {
  if (!projectId || typeof window === "undefined") return "";
  try {
    const raw = window.localStorage.getItem(completedRunStorageKey(projectId));
    if (!raw) return "";
    const parsed = JSON.parse(raw);
    return typeof parsed?.fingerprint === "string" ? parsed.fingerprint : "";
  } catch {
    return "";
  }
}

function writeCompletedRunFingerprint(projectId, fingerprint) {
  if (!projectId || !fingerprint || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      completedRunStorageKey(projectId),
      JSON.stringify({ fingerprint, completedAt: new Date().toISOString() })
    );
  } catch {
    // localStorage is best-effort; the in-memory guard still applies.
  }
}

function getSnapshotRunFingerprint(snapshot) {
  const completedPayload = snapshot?.reports_from_last_completed
    ? snapshot?.last_completed_input_payload
    : null;
  const config =
    completedPayload?.config ||
    snapshot?.config ||
    snapshot?.input?.config ||
    snapshot?.input_payload?.config ||
    snapshot?.run_input?.config ||
    snapshot?.payload?.config ||
    snapshot?.request?.config;
  if (!config) return "";
  const start =
    completedPayload?.start ||
    snapshot?.start ||
    snapshot?.start_date ||
    snapshot?.simulation_start ||
    snapshot?.input?.start ||
    snapshot?.input_payload?.start ||
    snapshot?.run_input?.start ||
    null;
  const end =
    completedPayload?.end ||
    snapshot?.end ||
    snapshot?.end_date ||
    snapshot?.simulation_end ||
    snapshot?.input?.end ||
    snapshot?.input_payload?.end ||
    snapshot?.run_input?.end ||
    null;
  if (!start || !end) return "";
  // The UI window uses date-only (YYYY-MM-DD via formatDateIso), but the backend
  // stores start/end as full datetimes (isoformat). Normalize to the day so the
  // reconstructed fingerprint can match the current-config fingerprint.
  const toDay = (v) => (typeof v === "string" ? v.slice(0, 10) : v);
  return buildRunConfigFingerprint(config, { start: toDay(start), end: toDay(end) });
}

export default function FeasibilitySimulation() {
  const navigate = useNavigate();
  const { currentProject, portfolio, save: saveProject } = useFile();
  const history = useHistory();
  const { isSessionReady, accessToken } = useAuth();
  const { demoProfile, refreshDemoProfile } = useDemo();
  const cfg = useMemo(() => history.present ?? DEFAULT_CONFIG, [history.present]);
  const feasibilityAccessRestrictionMessage = useMemo(
    () => getDemoActionDisabledMessage(demoProfile, "feasibility"),
    [demoProfile]
  );
  const revenueModelRestrictionMessage = useMemo(
    () => getRevenueModelDisabledMessage(demoProfile, cfg?.settlementType),
    [cfg?.settlementType, demoProfile]
  );
  const feasibilityDemoRestrictionMessage = useMemo(
    () => feasibilityAccessRestrictionMessage || revenueModelRestrictionMessage,
    [feasibilityAccessRestrictionMessage, revenueModelRestrictionMessage]
  );
  const demoExportDisabledMessage = useMemo(
    () => getDemoExportDisabledMessage(demoProfile, "feasibility"),
    [demoProfile]
  );
  const refreshDemoCounters = useCallback(async () => {
    try {
      await refreshDemoProfile();
    } finally {
      window.setTimeout(() => {
        void refreshDemoProfile();
      }, 1200);
    }
  }, [refreshDemoProfile]);
  // Which generating assets exist, so Scenario Inputs reports a weather source
  // for each. Shared with FeasibilityWeather so the two pages cannot disagree
  // about whether this project has wind.
  const simAssets = useMemo(() => deriveProjectAssets(cfg), [cfg]);
  const mergedCfg = useMemo(() => {
    const base = DEFAULT_CONFIG;
    const next = { ...base, ...cfg };
    next.feasibility = {
      ...base.feasibility,
      ...(cfg.feasibility || {}),
    };
    next.feasibility.technical = {
      ...base.feasibility.technical,
      ...(cfg.feasibility?.technical || {}),
    };
    return next;
  }, [cfg]);
  const feasibilityPayloadConfig = useMemo(
    () => prepareProjectConfigForPersistence(mergedCfg),
    [mergedCfg]
  );

  // Inject canonical marketStack into the outgoing payload.
  // Forces ida1/2/3 to false when the flag is off or the project is not GR —
  // even if stale local state exists. DAM defaults to true when not explicitly stored.
  const finalPayloadConfig = useMemo(() => {
    const base = feasibilityPayloadConfig;
    // supportsIdaMarket, not a GR-only check: this is the LAST gate before the
    // payload leaves the browser, so a hardcoded === "GR" here silently stripped
    // ida1/2/3 for FR/DE/NL/HU even after the user ticked them and the backend
    // accepted those markets. It also normalizes DE_LU -> DE for German projects.
    const idaEnabled = IDA_BANKABILITY_ENABLED && supportsIdaMarket(base?.biddingZone);
    const rs = getActiveIncludedRevenueStreams(base) || {};
    // Regulatory Category 11A (Greek RES + storage framework): the energy
    // settlement is PV at the Reference Price + BESS at DAM only, so the
    // outgoing market stack is forced to DAM even if stale local state
    // exists. The backend validates this independently.
    const regulatoryMode =
      base?.settlementType === "Arbitrage"
        ? String(base?.arbitrageMerchant?.regulatoryFramework?.mode || "none")
        : "none";
    const isRegulatory11A = regulatoryMode === "11A";
    const ida1 = !isRegulatory11A && idaEnabled ? Boolean(rs.ida1) : false;
    const ida2 = !isRegulatory11A && idaEnabled ? Boolean(rs.ida2) : false;
    const ida3 = !isRegulatory11A && idaEnabled ? Boolean(rs.ida3) : false;
    const hasIda = ida1 || ida2 || ida3;
    const balancing = !isRegulatory11A && Boolean(rs.balancing);
    const capacity = !isRegulatory11A && Boolean(rs.capacity);
    const go = !isRegulatory11A && Boolean(rs.go);
    const includedMarkets = {
      dam: hasIda || balancing ? true : Boolean(rs.dam ?? true),
      ida1,
      ida2,
      ida3,
      balancing,
      ancillary: false,
      capacity,
      go,
      other: false,
    };
    const includedRevenueStreams = {
      ...rs,
      ...includedMarkets,
      intraday: false,
    };
    const configWithCanonicalStreams = setActiveIncludedRevenueStreams(
      base,
      includedRevenueStreams
    );
    const payloadSimYear =
      Number(base?.feasibility?.simulationYear) || new Date().getFullYear() - 1;
    return {
      ...configWithCanonicalStreams,
      feasibility: {
        ...(configWithCanonicalStreams.feasibility || {}),
        simulationYear: payloadSimYear,
        simulationHorizonDays: String(deriveHorizonDays(payloadSimYear)),
      },
      marketParticipation: {
        dam: includedMarkets.dam,
        ida1: includedMarkets.ida1,
        ida2: includedMarkets.ida2,
        ida3: includedMarkets.ida3,
      },
      marketStack: {
        includedMarkets: buildIncludedMarketsList(includedMarkets),
        ...(balancing
          ? {
              balancing: buildBalancingV1Payload(
                getActiveBalancingV1Config(base),
                normalizeMarketZone(base?.biddingZone) || "GR"
              ),
            }
          : {}),
        ...(capacity
          ? {
              capacityPayments: buildCapacityPaymentsPayload(
                getActiveCapacityPaymentsConfig(base)
              ),
            }
          : {}),
        ...(go
          ? {
              guaranteesOfOrigin: buildGuaranteesOfOriginPayload(
                getActiveGuaranteesOfOriginConfig(base)
              ),
            }
          : {}),
      },
      ...(base?.settlementType === "PPA" && base?.ppa
        ? { ppa: buildPpaPayload(base.ppa) }
        : {}),
      // Behind-the-Meter: ship the commercial terms as numbers, and send every
      // BESS-page grid switch at the value the BTM settings imply — the same
      // answer the BESS page shows locked and the General page commits
      // (alignBtmSwitches in config/btm.js). The backend enforces the same.
      ...(isBtmSettlement(base?.settlementType)
        ? (() => {
            const standalone = isStandaloneBessSystem(base);
            const aligned = alignBtmSwitches(base);
            const btmPayload = buildBtmPayload(base?.btm, {
              standalone,
              country: base?.biddingZone,
            });
            return {
              btm: btmPayload,
              allowGridCharging: aligned.allowGridCharging,
              allowGridInjection: aligned.allowGridInjection,
              simultaneousInjectDraw: aligned.simultaneousInjectDraw,
              simultaneousGridPv: aligned.simultaneousGridPv,
              // Normalise the projection block into the shape the backend
              // normalizer expects (numbers, not the UI's blank strings).
              feasibility: {
                ...(base?.feasibility || {}),
                technical: {
                  ...(base?.feasibility?.technical || {}),
                  demandProjection: buildDemandProjectionPayload(
                    base?.feasibility?.technical?.demandProjection
                  ),
                },
              },
            };
          })()
        : {}),
    };
  }, [feasibilityPayloadConfig]);

  const [runStatus, setRunStatus] = useState({
    loading: false,
    status: "",
    error: "",
    success: "",
    runTime: "",
  });
  // Weather provenance stamped by the engine onto the run summary. Authoritative
  // for a stored result — it records the source actually used plus any
  // substituted values, which can differ from the current selection if that
  // changed after the run.
  const [runWeatherSource, setRunWeatherSource] = useState(null);
  const [pvRiskReady, setPvRiskReady] = useState(false);
  const [runPvRisk, setRunPvRisk] = useState(null);
  useEffect(() => { setRunPvRisk(null); setPvRiskReady(false); }, [currentProject?.id]);
  // Set the moment Execute is pressed, cleared as soon as the server reports
  // any state of its own. While it is up, a snapshot saying "not started"
  // cannot be newer than the run we just launched -- it is the poller having
  // asked before the POST landed -- and acting on it wipes the state we just
  // set. See the guard in the live poller below.
  const awaitingRunAckRef = useRef(false);
  // Kept after `cancelling` is released by the timeout above, so the page can
  // still say the request stands rather than pretending nothing was asked.
  const cancelRequestedRef = useRef(false);
  const preparingSceneRef = useRef(false);
  useEffect(() => () => {
    if (preparingSceneRef.current) cancelRequestedRef.current = true;
  }, [currentProject?.id]);
  const [reportStatus, setReportStatus] = useState({
    proposalDownloading: false,
    teaserDownloading: false,
    detailedTeaserDownloading: false,
    reportDownloading: false,
    generating: false,
    generated: false,
    error: "",
    success: "",
  });
  // Offer errors belong next to the clicked export button, not at the bottom of
  // all report cards. Keep them visible until a retry or a change of offer.
  const [commercialOfferFeedback, setCommercialOfferFeedback] = useState(null);
  const [teaserPublish, setTeaserPublish] = useState({
    loading: false,
    url: "",
    views: 0,
    copied: false,
    revoking: false,
    revoked: false,
    error: "",
  });
  const [detailedTeaserPublish, setDetailedTeaserPublish] = useState({
    loading: false,
    url: "",
    views: 0,
    copied: false,
    revoked: false,
    error: "",
  });
  const [simulationStatus, setSimulationStatus] = useState({
    status: "Not started",
    progress: 0,
    phase: "",
    message: "",
    lastRun: null,
    lastUpdated: null,
    reportsAvailable: false,
    reportsFromLastCompleted: false,
    lastCompletedAt: null,
  });
  const [completedRunFingerprint, setCompletedRunFingerprint] = useState("");
  const { isGreek } = useLanguage();
  useEffect(() => {
    setCommercialOfferFeedback(null);
  }, [currentProject?.id, cfg.commercialOffer, isGreek]);
  const [triggerToastOpen, setTriggerToastOpen] = useState(false);
  const [unsavedDialogOpen, setUnsavedDialogOpen] = useState(false);
  // The results card opens from a button, so the page looks as before until
  // the user asks for it.
  const [studyResultsOpen, setStudyResultsOpen] = useState(false);
  const [preRunWarningsOpen, setPreRunWarningsOpen] = useState(false);
  // The warnings the user chose to run with, per project; asked again only
  // when they change. Kept in the browser so a deliberate loan or MV
  // connection is not questioned on every visit.
  const acceptedWarningsRef = useRef("");
  const preRunWarningsStorageKey = `pythia.preRunWarningsAccepted.${currentProject?.id || "unsaved"}`;
  const acceptedPreRunWarnings = () => {
    try { return window.localStorage.getItem(preRunWarningsStorageKey) ?? acceptedWarningsRef.current; }
    catch { return acceptedWarningsRef.current; }
  };
  const acceptPreRunWarnings = (key) => {
    acceptedWarningsRef.current = key;
    try { window.localStorage.setItem(preRunWarningsStorageKey, key); } catch { /* private mode: this visit only */ }
  };
  // Why the automatic save before a run did not go through (empty: the
  // project has never been saved, so there is nothing to save into).
  const [unsavedSaveError, setUnsavedSaveError] = useState("");
  const [savingBeforeRun, setSavingBeforeRun] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  // The status read on opening the page failed after its retries. The page
  // then knows nothing about the run, and says so, instead of showing the
  // initial "Not started" as if it were the server's answer.
  const [statusLoadError, setStatusLoadError] = useState(false);
  const streamRef = useRef(null);
  const lastStreamUpdateRef = useRef(0);
  const lastRunRefreshRef = useRef(false);
  const completedRunRecoveryEpoch = useRef(0);
  useEffect(() => () => { completedRunRecoveryEpoch.current += 1; }, [currentProject?.id]);
  const streamActiveRef = useRef(false);
  const streamHasPayloadRef = useRef(false);
  const activeRunFingerprintRef = useRef("");
  // The toast hangs off the row, not off the Execute button: the button is
  // only half the row once a run starts, and anchoring to it put the toast
  // exactly where the Cancel button appears.
  const buttonRowRef = useRef(null);

  const yearFieldLabelSx = {
    color: "primary.main",
    fontSize: "0.95rem",
    fontWeight: 700,
    lineHeight: 1.25,
    minWidth: 0,
  };
  const projectDateSx = {
    px: 1.5,
    py: 0.75,
    minWidth: 150,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1.5,
    backgroundColor: "action.hover",
  };
  const reportCardSx = {
    display: "flex", flexDirection: "column", alignItems: "flex-start",
    justifyContent: "flex-start", gap: 0.5, p: 1, minWidth: 0,
    borderRadius: 1.5, border: "1px solid", borderColor: "divider",
    bgcolor: "background.paper",
    "& .MuiButton-root": {
      whiteSpace: "normal",
      minHeight: { xs: 40, lg: 34 },
      py: { xs: 0.75, lg: 0.375 },
      px: 1.25,
      fontSize: { xs: "0.875rem", lg: "0.8125rem" },
    },
    "& .MuiTypography-root": { overflowWrap: "anywhere" },
  };
  const fieldSx = {
    "& .MuiInputBase-root": { minHeight: 44 },
    "& .MuiInputBase-input": { py: 1.25 },
  };
  const placeholderSx = {
    typography: "body2",
    color: "text.primary",
  };
  const formatStreamMessage = (raw) => {
    const text = String(raw || "").trim();
    if (!text) return "";
    const lower = text.toLowerCase();
    const isBadGateway = lower.includes("502") || lower.includes("bad gateway");
    const isServiceUnavailable =
      lower.includes("503") || lower.includes("service unavailable");
    const isEntso = lower.includes("entso") || lower.includes("entsoe");
    const isTransparency =
      lower.includes("transparency") || lower.includes("web-api.tp.entsoe.eu");
    const isMeteo = lower.includes("open-meteo") || lower.includes("archive-api");
    if ((isBadGateway || isServiceUnavailable) && (isEntso || isTransparency)) {
      return "DAM Energy prices API fetch failed (ENTSO-E Transparency Platform). Please retrigger the simulation.";
    }
    if (isBadGateway && (isEntso || isMeteo)) {
      return "API fetching error occurred. Please retrigger the simulation.";
    }
    return text;
  };
  function setPathInObject(obj, path, value) {
    const keys = path.split(".");
    const clone = { ...obj };
    let cur = clone;
    for (let i = 0; i < keys.length - 1; i++) {
      const k = keys[i];
      cur[k] = { ...(cur[k] ?? {}) };
      cur = cur[k];
    }
    cur[keys[keys.length - 1]] = value;
    return clone;
  }
  const setFeasibility = (path, value) => {
    history.commit(setPathInObject(mergedCfg, `feasibility.${path}`, value));
  };
  const formatTimestamp = (iso) => {
    if (!iso) return "\u2014";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "\u2014";
    return d.toLocaleString("en-GB");
  };
  const localTimeZone =
    Intl.DateTimeFormat().resolvedOptions().timeZone || "Local time";
  const mapStatusLabel = (status) => {
    switch (status) {
      case "running":
        return "Running";
      case "pending":
        return "Queued";
      case "success":
        return "Finished";
      case "failed":
        return "Failed";
      case "not_started":
        return "Not started";
      case "cancelled":
        return "Cancelled";
      default:
        return "Queued";
    }
  };
  const statusTone = (status) => {
    switch (status) {
      case "Running":
        return { label: "Running", color: "#2563eb", bg: "rgba(37,99,235,0.15)" };
      case "Queued":
        return { label: "Queued", color: "#f59e0b", bg: "rgba(245,158,11,0.16)" };
      case "Finished":
        return { label: "Finished", color: "#16a34a", bg: "rgba(22,163,74,0.16)" };
      case "Failed":
        return { label: "Failed", color: "#dc2626", bg: "rgba(220,38,38,0.16)" };
      case "Paused":
        return { label: "Paused", color: "#64748b", bg: "rgba(100,116,139,0.14)" };
      case "Cancelled":
        return { label: "Cancelled", color: "#64748b", bg: "rgba(100,116,139,0.14)" };
      default:
        return { label: "Not started", color: "text.primary", bg: "rgba(148,163,184,0.18)" };
    }
  };
  // Phase and message come off the worker in English and are shown verbatim in
  // a node that also carries the value, so the text-node translator can never
  // match them. Both are closed sets, so both are written out per language here.
  const updateSimulationStatus = (patch) => {
    setSimulationStatus((prev) => {
      const next = {
        ...prev,
        ...patch,
        lastUpdated: new Date().toISOString(),
      };
      return next;
    });
  };
  const displayedProgress =
    simulationStatus.status === "Queued"
      ? 0
      : Math.min(simulationStatus.status === "Finished" ? 100 : 99, Math.max(0, Number(simulationStatus.progress) || 0));
  const displayedProgressLabel = `${simulationStatus.status === "Finished" ? 100 : Math.floor(displayedProgress)}%`;

  const previousYear = defaultSimulationYear();
  const simYear = Number(mergedCfg.feasibility?.simulationYear) || previousYear;
  // Shared with the Weather page, so the year you can inspect the platform's
  // weather for is always a year you can actually run.
  const availableYears = useMemo(() => simulationYears(), []);
  const referenceYear = simYear;
  const operationYear = operatingStartYear(mergedCfg);
  const horizonValue = deriveHorizonDays(referenceYear);
  const referenceYearStartDate = useMemo(
    () => new Date(referenceYear, 0, 1),
    [referenceYear]
  );
  const referenceYearEndDate = useMemo(() => {
    const d = new Date(referenceYearStartDate);
    d.setDate(d.getDate() + horizonValue - 1);
    return d;
  }, [referenceYearStartDate, horizonValue]);
  const projectPeriod = useMemo(
    () => projectLifetimeDates(validOperatingStartYear(operationYear) ? operationYear : NaN, mergedCfg.feasibility?.settlementPeriod),
    [mergedCfg.feasibility?.settlementPeriod, operationYear]
  );
  const formatDate = (d) => {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };
  const formatDateIso = (d) => {
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${yyyy}-${mm}-${dd}`;
  };
  const currentRunWindow = useMemo(
    () => ({
      start: formatDateIso(referenceYearStartDate),
      end: formatDateIso(referenceYearEndDate),
    }),
    [referenceYearStartDate, referenceYearEndDate]
  );
  const currentRunFingerprint = useMemo(
    () => buildRunConfigFingerprint(finalPayloadConfig, currentRunWindow),
    [finalPayloadConfig, currentRunWindow]
  );
  const currentIncludedMarkets = useMemo(
    () => getComparableIncludedMarkets(finalPayloadConfig),
    [finalPayloadConfig]
  );
  const installedBessPowerMw = useMemo(
    () => deriveInstalledBessPowerMw(finalPayloadConfig),
    [finalPayloadConfig]
  );
  const feasibilityValidation = useMemo(
    () =>
      validateBalancingV1Config({
        enabled: Boolean(currentIncludedMarkets.balancing),
        includedMarkets: currentIncludedMarkets,
        balancingConfig: finalPayloadConfig?.marketStack?.balancing,
        installedBessPowerMw,
        market: normalizeMarketZone(finalPayloadConfig?.biddingZone) || "GR",
      }),
    [currentIncludedMarkets, finalPayloadConfig, installedBessPowerMw]
  );
  // Behind-the-Meter cannot run without a demand profile and complete retail
  // tariffs — the backend raises on exactly these conditions, so catching them
  // here turns a failed queued run into an inline message.
  const btmBlockingErrors = useMemo(() => {
    if (!isBtmSettlement(finalPayloadConfig?.settlementType)) return [];
    return [
      ...Object.values(validateBtm(normalizeBtm(finalPayloadConfig), finalPayloadConfig)),
      ...Object.values(
        validateDemandProjection(
          normalizeDemandProjection(finalPayloadConfig),
          finalPayloadConfig
        )
      ),
    ];
  }, [finalPayloadConfig]);
  // ---- what this run will actually cover ---------------------------------
  // The scope of a study is spread over five pages: the asset type and its
  // capacity on Asset Setup, the bidding zone and market stack on Market Data,
  // the weather on Weather Data, the year here. Pressing Execute with none of
  // it in view is how a study gets run against the wrong market stack and only
  // found out from the revenue. `assetSummary` is the same derivation the
  // project overview uses, so the two cannot describe one asset differently.
  // Whether a run is in flight, derived rather than read off one field.
  // `runStatus.loading` has FIVE writers -- the click, the POST reply, the
  // 2.5 s poller, the SSE payload handler and two error paths -- and they race:
  // a stream ticket that fails once, or a poll that lands between two states,
  // sets it false for a moment and the button flickers out of its busy state
  // and back. The simulation's own status is the slower, steadier signal, so
  // the two together say "busy" whenever either does.
  const runInFlight =
    runStatus.loading ||
    simulationStatus.status === "Queued" ||
    simulationStatus.status === "Running";

  const runScope = useMemo(() => assetSummary(finalPayloadConfig), [finalPayloadConfig]);
  const runIsBtm = isBtmSettlement(finalPayloadConfig?.settlementType);
  // Virtual net billing: no teasers.
  const runIsVnb = runIsBtm && isVirtualNetBilling(normalizeBtm(finalPayloadConfig));
  const runRegulatoryMode = commercialProjectOfferMode(finalPayloadConfig);
  // The results card reads the detailed proposal with exactly the terms its
  // download sends (handleDownloadCommercialOffer), so the two never differ.
  const studyResultsTerms = useMemo(() => normalizeCommercialOffer(cfg.commercialOffer, {
    systemType: cfg.systemType, reportLanguage: getReportLanguage(cfg),
    regulatoryMode: runRegulatoryMode,
  }), [cfg, runRegulatoryMode]);
  const btmValuation = useMemo(
    () => (runIsBtm ? btmValuationLabels(finalPayloadConfig?.btm, finalPayloadConfig) : []),
    [runIsBtm, finalPayloadConfig]
  );
  const activeMarkets = useMemo(
    () => MARKET_LABELS.filter(([key]) => currentIncludedMarkets[key]).map(([, label]) => label),
    [currentIncludedMarkets]
  );

  // ---- degradation, beside the lifetime it applies over --------------------
  // The rate is set on Technical Assumptions and the horizon here, and the two
  // multiply out into the whole financial projection. Reading them on one page
  // is what lets a 25-year curve under a 20-year study be noticed at all.
  const degradation = useMemo(() => {
    const curve = describeDegradation(finalPayloadConfig?.pvDegradation?.retentionPct);
    const flat = Number(finalPayloadConfig?.annualDegFactor);
    const lifetime = Number(finalPayloadConfig?.feasibility?.settlementPeriod);
    if (curve) {
      return {
        text: curve.uniform
          ? isGreek
            ? `${curve.firstYearPct.toFixed(2)}% το 1ο έτος, έπειτα ${curve.annualPct.toFixed(2)} ποσοστιαίες μονάδες/έτος · τέλος καμπύλης (έτος ${curve.years}): ${curve.finalRetentionPct.toFixed(1)}% της ονομαστικής ισχύος`
            : `${curve.firstYearPct.toFixed(2)}% in year 1, then ${curve.annualPct.toFixed(2)} percentage points/year · end of curve (year ${curve.years}): ${curve.finalRetentionPct.toFixed(1)}% of nameplate`
          : isGreek
            ? `καμπύλη ανά έτος · τέλος καμπύλης (έτος ${curve.years}): ${curve.finalRetentionPct.toFixed(1)}% της ονομαστικής ισχύος`
            : `year-by-year curve · end of curve (year ${curve.years}): ${curve.finalRetentionPct.toFixed(1)}% of nameplate`,
        // Only the short curve is a problem. A module warranted for 30 years
        // under a 20-year study is the ordinary case -- the study uses the
        // first 20 rows of the table and the rest is simply not reached --
        // and flagging it put a permanent warning under a correct setup,
        // which is how a reader learns to stop reading the warnings.
        mismatch:
          Number.isFinite(lifetime) && lifetime > 0 && curve.years < Math.round(lifetime)
            ? "shortCurve"
            : "",
        curveYears: curve.years,
        studyYears: Number.isFinite(lifetime) ? Math.round(lifetime) : null,
      };
    }
    if (Number.isFinite(flat) && flat > 0) {
      const retained = Number.isFinite(lifetime) && lifetime > 0
        ? Math.pow(1 - flat / 100, lifetime - 1) * 100
        : null;
      return {
        curveYears: null,
        studyYears: Number.isFinite(lifetime) ? Math.round(lifetime) : null,
        text: retained === null
          ? isGreek
            ? `${flat.toFixed(2)}%/έτος (σταθερό ποσοστό, χωρίς καμπύλη)`
            : `${flat.toFixed(2)}% per year (single rate, no curve set)`
          : isGreek
            ? `${flat.toFixed(2)}%/έτος (σταθερό ποσοστό, χωρίς καμπύλη) · ${retained.toFixed(1)}% της ονομαστικής ισχύος στο έτος ${Math.round(lifetime)}`
            : `${flat.toFixed(2)}% per year (single rate, no curve set) · ${retained.toFixed(1)}% of nameplate in year ${Math.round(lifetime)}`,
        mismatch: "",
      };
    }
    return { text: "", mismatch: "noDegradation", curveYears: null, studyYears: null };
  }, [finalPayloadConfig, isGreek]);

  // ---- do we have prices for the year being asked for? ---------------------
  // A reference year the market data only half covers still runs to completion:
  // the missing hours simply price at nothing, and the study earns less for a
  // reason that appears nowhere on the results. Asked before the run, once per
  // year-and-zone, and never allowed to block anything if it fails.
  const priceZone = useMemo(
    () => normalizeMarketZone(finalPayloadConfig?.biddingZone) || "GR",
    [finalPayloadConfig]
  );
  const [priceCoverage, setPriceCoverage] = useState(null);
  useEffect(() => {
    let alive = true;
    setPriceCoverage(null);
    if (!simYear || !priceZone) return undefined;
    fetchDamPriceCoverage({ market: priceZone, year: simYear }, accessToken)
      .then((data) => { if (alive) setPriceCoverage({ zone: priceZone, year: simYear, data }); })
      .catch(() => { if (alive) setPriceCoverage({ zone: priceZone, year: simYear, error: true }); });
    return () => { alive = false; };
  }, [priceZone, simYear, accessToken]);
  const coverage =
    priceCoverage && priceCoverage.zone === priceZone && priceCoverage.year === simYear
      ? priceCoverage
      : null;

  // Scene shading missing or out of date: the notice above Execute calculates
  // it here -- a step of its own, before the run, rather than a hidden first
  // step of the run that leaving the page would lose -- or lets the study run
  // without it, said so, by choice. It does not block.
  const shadingPending = useMemo(() => sceneShadingPending(finalPayloadConfig), [finalPayloadConfig]);
  const sceneIssues = useMemo(() => sceneSimulationIssues(finalPayloadConfig, isGreek), [finalPayloadConfig, isGreek]);
  const shadingPendingMessage = shadingPending.length ? sceneIssues[sceneIssues.length - 1] : null;
  const feasibilityBlockingErrors = useMemo(
    () => [
      ...(!validOperatingStartYear(operationYear) ? [isGreek ? "Εισάγετε έγκυρο έτος έναρξης λειτουργίας (1900–2200)." : "Enter a valid operating start year (1900–2200)."] : []),
      ...(finalPayloadConfig?.merchantRevenueProjection?.mode === "forward_curve_redispatch" &&
        finalPayloadConfig?.merchantRevenueProjection?.forwardCurve?.housePathStartYear != null &&
        Number(finalPayloadConfig.merchantRevenueProjection.forwardCurve.housePathStartYear) !== operationYear
        ? [isGreek ? "Η φορτωμένη καμπύλη τιμών αφορά άλλο έτος έναρξης. Φορτώστε την ξανά στις οικονομικές παραδοχές." : "The loaded price path uses a different operating start year. Reload it in the economic assumptions."] : []),
      ...(feasibilityValidation.errors || []), ...btmBlockingErrors,
      ...sceneIssues.filter((message) => message !== shadingPendingMessage),
      ...(assetTypeDefinition(cfg.systemType)?.hasBess && finalPayloadConfig?.bess?.topology === "dc-coupled" ? [
        ...(!simAssets.hasPv || !assetTypeDefinition(cfg.systemType)?.hasBess || !isBtmSettlement(finalPayloadConfig?.settlementType)
          ? ["DC coupling currently requires PV and battery in a behind-the-meter study."] : []),
        ...(!(Number(finalPayloadConfig?.bess?.dcDcEfficiencyPct) > 0 && Number(finalPayloadConfig?.bess?.dcDcEfficiencyPct) <= 100)
          ? ["Enter the battery DC/DC efficiency (greater than 0 and at most 100%)."] : []),
        ...(!(Number(finalPayloadConfig?.bess?.dcPortChargeKw) > 0)
          ? ["Enter the hybrid inverter battery-port charge limit in kW DC."] : []),
        ...(!(Number(finalPayloadConfig?.bess?.dcPortDischargeKw) > 0)
          ? ["Enter the hybrid inverter battery-port discharge limit in kW DC."] : []),
        ...(finalPayloadConfig?.allowGridCharging
          ? ["DC coupling currently supports PV-only battery charging; turn off grid charging."] : []),
        ...(finalPayloadConfig?.hasTransformer || (finalPayloadConfig?.transformerDets?.chainLossMode || "off") !== "off"
          ? ["DC coupling currently requires no transformer or POI loss chain."] : []),
        ...(finalPayloadConfig?.batteryPerformance?.systemRtePct !== "" && finalPayloadConfig?.batteryPerformance?.systemRtePct != null
          ? ["Clear the whole-system RTE override for DC coupling."] : []),
        ...((finalPayloadConfig?.batteryPerformance?.degradationMode || "legacy") === "legacy"
          ? ["DC coupling requires a non-legacy battery degradation model for the multi-year study."] : []),
      ] : []),
      ...(assetTypeIssue(cfg) ? [assetTypeIssue(cfg)] : []),
      ...(studyAssetIssue(finalPayloadConfig) ? [studyAssetIssue(finalPayloadConfig)] : []),
    ],
    [feasibilityValidation, btmBlockingErrors, cfg, finalPayloadConfig, simAssets, sceneIssues, shadingPendingMessage, operationYear, isGreek]
  );
  // What the engine would certainly reject, with where to fix it; and the
  // probable leftover defaults, asked about once before a run (preRunChecks.js).
  const preRunBlockerList = useMemo(() => preRunBlockers(finalPayloadConfig, isGreek), [finalPayloadConfig, isGreek]);
  // The cost still on the platform's indicative prices: the results card says so.
  const studyIndicativePrices = useMemo(() => indicativePricesInUse(finalPayloadConfig), [finalPayloadConfig]);
  const preRunWarningList = useMemo(() => preRunWarnings(finalPayloadConfig, isGreek), [finalPayloadConfig, isGreek]);
  const feasibilityBlockingErrorText = feasibilityBlockingErrors
    .map((message) => isGreek ? translateLegacyText(message) : message)
    .join(" ");
  const [shadingCalc, setShadingCalc] = useState(null);   // { progress } while running, { error } after a failure
  const shadingCancelRef = useRef(false);
  const shadingNoticeRef = useRef(null);
  // Set by "Run without layout shading"; read once by the run.
  const runWithoutShadingRef = useRef(false);
  const calculateSceneShading = async () => {
    shadingCancelRef.current = false;
    setShadingCalc({ progress: 0 });
    try {
      const module = await getModuleDetails(finalPayloadConfig.read_module, accessToken);
      const prepared = await prepareSceneInBackground(finalPayloadConfig, { module, projectId: currentProject?.id,
        shouldCancel: () => shadingCancelRef.current,
        onProgress: (p) => setShadingCalc({ progress: p }) });
      const measured = [prepared.pv_design, ...(prepared.orientations || []).slice(1)];
      let next = mergedCfg;
      orientationsFromConfig(mergedCfg).forEach((plane, i) => {
        const evidence = measured[i]?.obstacleShading;
        if (evidence?.table && !evidence.stale) next = saveOrientationShading(next, plane.id, evidence);
      });
      history.commit(next);
      setShadingCalc(null);
    } catch (err) {
      // Its reason in the page's language, never the engine's own wording.
      setShadingCalc(shadingCancelRef.current || isSceneCalculationCancelled(err) ? null
        : { error: scenePreparationMessage(err, isGreek) });
    }
  };
  const currentUsesOnlyDam =
    currentIncludedMarkets.dam &&
    !currentIncludedMarkets.ida1 &&
    !currentIncludedMarkets.ida2 &&
    !currentIncludedMarkets.ida3 &&
    !currentIncludedMarkets.balancing &&
    !currentIncludedMarkets.ancillary &&
    !currentIncludedMarkets.capacity &&
    !currentIncludedMarkets.go &&
    !currentIncludedMarkets.other;
  const hasLegacyDamOnlyCompletedRun = Boolean(
    currentProject?.id &&
      !completedRunFingerprint &&
      currentUsesOnlyDam &&
      (runStatus.status === "success" || simulationStatus.status === "Finished")
  );
  const hasMatchingCompletedRun = Boolean(
    currentProject?.id &&
      ((completedRunFingerprint && completedRunFingerprint === currentRunFingerprint) ||
        hasLegacyDamOnlyCompletedRun)
  );
  const reportConfigIsStale = Boolean(currentProject?.id) && !hasMatchingCompletedRun;
  // Kept as a named hook point so the seven handlers read the same, but a
  // stale configuration is no longer a reason to refuse: see the note on
  // STALE_CONFIG_WARNING.
  const blockStaleReportAction = useCallback(() => false, []);
  const markCompletedRunFingerprint = useCallback(
    (fingerprint) => {
      if (!currentProject?.id || !fingerprint) return;
      setCompletedRunFingerprint(fingerprint);
      writeCompletedRunFingerprint(String(currentProject.id), fingerprint);
    },
    [currentProject?.id]
  );
  // The run that just finished was not necessarily launched by THIS page
  // instance. Leave the page while a run is in flight and come back, and the
  // remount reopens the stream with an empty launch-time fingerprint; the
  // completion event then had nothing to record, the poller had already
  // stopped (the run is no longer in flight), and the report buttons stayed
  // locked behind "configuration changed" for a run whose configuration had
  // not changed. The backend keeps the exact config each run was posted with,
  // so when the launch-time value is missing the fingerprint is rebuilt from
  // that instead -- the same reconstruction the on-mount snapshot uses.
  const recoverCompletedRunFingerprint = useCallback(async (recoverFingerprint = true) => {
    const epoch = completedRunRecoveryEpoch.current;
    if (!currentProject?.id) return;
    // Two attempts: the stream can announce completion a moment before the
    // status row it is read back from is visible to a fresh request.
    for (let attempt = 0; attempt < 2; attempt += 1) {
      if (epoch !== completedRunRecoveryEpoch.current) return;
      try {
        const snapshot = await metenApi.getFeasibilityStatus(String(currentProject.id));
        if (epoch !== completedRunRecoveryEpoch.current) return;
        const status = String(snapshot?.status || "").toLowerCase().trim();
        if (status === "success" || status === "completed") {
          const fingerprint = getSnapshotRunFingerprint(snapshot);
          if (recoverFingerprint && fingerprint) markCompletedRunFingerprint(fingerprint);
          setSimulationStatus((prev) => ({ ...prev, lastCompletedAt: snapshot?.last_completed_at || null }));
          return;
        }
      } catch (err) {
        console.error("Failed to recover the completed-run fingerprint.", err);
      }
      if (attempt === 0) await new Promise((resolve) => setTimeout(resolve, 1500));
    }
  }, [currentProject?.id, markCompletedRunFingerprint]);

  const triggerDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename || "download";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const extractErrorMessage = (err, fallback) => {
    const raw = err?.message || err?.toString?.() || "";
    const formatted = formatApiError(err, fallback, { quota: "feasibility" });
    if (
      err?.status === 403 &&
      demoProfile?.demoLocked &&
      /failed to fetch|networkerror|network error|load failed/i.test(String(raw || formatted))
    ) {
      return "Demo account is locked.";
    }
    return formatted;
  };

  const openFeasibilityStream = useCallback(async (feasibilityId) => {
    if (!feasibilityId) return;
    const baseUrl = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
    const streamUrl = new URL(
      `/feasibility/${encodeURIComponent(String(feasibilityId))}/stream`,
      baseUrl
    );
    if (streamRef.current && typeof streamRef.current.close === "function") {
      streamRef.current.close();
    }
    streamRef.current = null;
    let streamTicket;
    try {
      streamTicket = await requestFeasibilityStreamTicket(feasibilityId, accessToken);
    } catch (err) {
      console.error(
        "Feasibility stream ticket request failed:",
        err?.status || err?.message || "unknown error"
      );
      // No live stream is not a stopped run. Clearing `loading` here made the
      // run look finished (Execute enabled, no progress) and switched off the
      // status poll, which only runs while a run is in flight. The poll
      // carries the progress instead.
      streamActiveRef.current = false;
      streamHasPayloadRef.current = false;
      return;
    }
    streamUrl.searchParams.set("stream_ticket", streamTicket);
    const es = new EventSource(streamUrl.toString());
    streamRef.current = es;
    streamActiveRef.current = true;
    streamHasPayloadRef.current = false;
    const normalizeStatus = normalizeRunStatus;
    const handlePayload = (payload) => {
      if (preparingSceneRef.current) return;
      const normalized = normalizeStatus(payload?.status);
      const nextPhase = payload?.phase || "";
      const rawMessage =
        payload?.message ||
        (normalized === "failed" ? payload?.error_message || "Simulation failed." : "");
      const nextMessage =
        String(rawMessage || "").trim().toLowerCase() === "completed"
          ? "Results are available."
          : formatStreamMessage(rawMessage);
      let progressValue = Number.isFinite(Number(payload?.progress))
        ? Number(payload?.progress)
        : null;
      // An event without a progress figure keeps the last one; it used to
      // reset the bar to 0 % mid-run.
      if (normalized === "pending") {
        progressValue = 0;
      }
      const keepLast = {
        ...(progressValue !== null ? { progress: progressValue } : {}),
        ...(payload?.phase ? { phase: nextPhase } : {}),
        ...(rawMessage ? { message: nextMessage || "" } : {}),
      };
      const next = {
        loading: normalized === "pending" || normalized === "running",
        status: normalized,
        error:
          normalized === "failed"
            ? "Simulation failed. Check message field for more details."
            : "",
        success: normalized === "success" ? "Simulation completed successfully." : "",
        runTime: normalized === "success" ? String(payload?.run_time || "") : "",
      };
      setRunStatus(prev => ({ ...next, cancelling: next.loading ? prev.cancelling : false }));
      if (normalized === "success") setRunPvRisk(payload?.pv_financial_risk || payload?.summary?.pv_financial_risk || null);
      if (payload?.weather_source) {
        setRunWeatherSource(payload.weather_source);
      }
      streamHasPayloadRef.current = true;
      lastStreamUpdateRef.current = Date.now();
      if (normalized === "running") {
        updateSimulationStatus({ status: "Running", ...keepLast });
      }
      if (normalized === "pending") {
        updateSimulationStatus({ status: "Queued", ...keepLast });
      }
      if (normalized === "success") {
        void refreshDemoCounters();
        // Launched from this page instance: the exact value the gate compares
        // against. Otherwise (remounted mid-run) rebuild it from the config
        // the backend stored for the run.
        if (activeRunFingerprintRef.current) {
          markCompletedRunFingerprint(activeRunFingerprintRef.current);
        }
        // Read the recorded completion time; the browser's receipt time is not
        // the timestamp of the results (especially after reconnecting).
        void recoverCompletedRunFingerprint(!activeRunFingerprintRef.current);
        updateSimulationStatus({
          status: "Finished",
          progress: 100,
          phase: nextPhase || "",
          message: nextMessage || "",
          reportsAvailable: true,
          reportsFromLastCompleted: false,
          lastCompletedAt: payload?.last_completed_at || null,
        });
      }
      if (normalized === "failed" || normalized === "cancelled") {
        updateSimulationStatus({
          status: normalized === "cancelled" ? "Cancelled" : "Failed",
          ...(payload?.progress != null ? { progress: progressValue } : {}),
          phase: nextPhase || "",
          message: nextMessage || "",
        });
      }
      if (["success", "failed", "cancelled"].includes(normalized)) {
        es.close();
        streamRef.current = null;
        streamActiveRef.current = false;
      }
    };

    es.addEventListener("status", (event) => {
      try {
        const payload = JSON.parse(event.data || "{}");
        handlePayload(payload);
      } catch (err) {
        console.error("Feasibility stream parse error", err);
      }
    });
    // A dropped stream (a proxy timeout, a redeploy, the laptop sleeping) is
    // not the end of the run. It used to set `loading: false` and print a
    // connection error: the page then showed the run as stopped, enabled
    // Execute and turned off the 2.5 s status poll, which only runs while a
    // run is in flight. Close it and let the poll carry the run to its end.
    es.onerror = () => {
      console.error("Feasibility stream error");
      es.close();
      if (streamRef.current === es) streamRef.current = null;
      streamActiveRef.current = false;
      streamHasPayloadRef.current = false;
    };
  }, [
    accessToken,
    markCompletedRunFingerprint,
    recoverCompletedRunFingerprint,
    refreshDemoCounters,
  ]);

  const handleDownloadProposal = async () => {
    if (!currentProject?.id || reportStatus.proposalDownloading) return;
    if (blockStaleReportAction()) return;
    if (demoExportDisabledMessage) {
      setReportStatus((prev) => ({
        ...prev,
        error: `Error: ${demoExportDisabledMessage}`,
        success: "",
      }));
      return;
    }
    setReportStatus((prev) => ({
      ...prev,
      proposalDownloading: true,
      error: "",
      success: "",
    }));
    try {
      const { blob, filename } = await metenApi.downloadFeasibilityProposal(
        String(currentProject.id)
      );
      triggerDownload(blob, filename || `feasibility_proposal_${currentProject.id}.pdf`);
      setReportStatus((prev) => ({
        ...prev,
        proposalDownloading: false,
        success: "Proposal downloaded.",
      }));
    } catch (err) {
      const message = extractErrorMessage(err, "Failed to download proposal.");
      const cleanMessage = message.toLowerCase().includes("proposal")
        ? "Error: Proposal report is not generated"
        : `Error: ${message}`;
      setReportStatus((prev) => ({
        ...prev,
        proposalDownloading: false,
        error: cleanMessage,
      }));
    }
  };

  const handleDownloadCommercialOffer = async (options) => {
    if (!currentProject?.id || reportStatus.commercialOfferDownloading) return false;
    if (blockStaleReportAction() || demoExportDisabledMessage) return false;
    const word = options?.format === "docx";
    // The Word button has its own progress and message next to it.
    const kind = word ? "proposal-docx" : options?.kind || "offer";
    const normalizedTerms = normalizeCommercialOffer(cfg.commercialOffer, {
      systemType: cfg.systemType, reportLanguage: getReportLanguage(cfg),
      regulatoryMode: runRegulatoryMode,
    });
    // Attachments omit customer/contract terms, but use the same VAT assumptions
    // as the offers, so the investment and payback cannot disagree.
    const benefitTerms = { pricing: { vat_pct: normalizedTerms.pricing.vat_pct,
      main: { vat_recoverable_pct: normalizedTerms.pricing.main.vat_recoverable_pct },
      pv_only: { vat_recoverable_pct: normalizedTerms.pricing.pv_only.vat_recoverable_pct } } };
    const validation = commercialOfferValidation(kind === "benefit" ? benefitTerms : normalizedTerms, { isGreek });
    if (validation) {
      setCommercialOfferFeedback({ kind, error: validation, canEdit: true });
      return false;
    }
    setCommercialOfferFeedback({ kind });
    setReportStatus((prev) => ({ ...prev, commercialOfferDownloading: true, error: "", success: "" }));
    try {
      const benefit = options?.kind === "benefit";
      const args = [String(currentProject.id), benefit ? benefitTerms : normalizedTerms];
      if (options) args.push(options);
      const { blob, filename } = await metenApi.downloadCommercialOffer(...args);
      triggerDownload(blob, filename || `${options?.kind === "proposal" ? "commercial_proposal" : benefit ? "economic_benefit" : "commercial_offer"}_${currentProject.id}.${word ? "docx" : "pdf"}`);
      setReportStatus((prev) => ({ ...prev, commercialOfferDownloading: false }));
      setCommercialOfferFeedback({ kind, success: benefit
          ? (isGreek ? "Το παράρτημα οικονομικού οφέλους κατέβηκε." : "Economic-benefit attachment downloaded.")
          : word
            ? (isGreek ? "Η αναλυτική εμπορική προσφορά σε Word κατέβηκε." : "Detailed commercial proposal downloaded as Word.")
          : kind === "proposal"
            ? (isGreek ? "Η αναλυτική εμπορική προσφορά κατέβηκε." : "Detailed commercial proposal downloaded.")
            : (isGreek ? "Η οικονομική προσφορά κατέβηκε." : "Commercial offer downloaded.") });
      return true;
    } catch (err) {
      const raw = extractErrorMessage(err, "Failed to download the commercial offer.");
      const message = /new behind-the-meter runs|new supported runs|re-run the study after deployment/i.test(raw)
        ? (isGreek ? (isMerchantCommercialMode(runRegulatoryMode)
          ? "Η προσφορά Merchant Arbitrage είναι διαθέσιμη για νέες εκτελέσεις της μελέτης. Εκτελέστε ξανά τη μελέτη μετά την αναβάθμιση."
          : runRegulatoryMode
          ? `Η εμπορική προσφορά ${runRegulatoryMode === "11B" ? "11Β" : "11Α"} είναι διαθέσιμη για νέες εκτελέσεις της μελέτης. Εκτελέστε ξανά τη μελέτη μετά την αναβάθμιση.`
          : "Η οικονομική προσφορά είναι διαθέσιμη για νέες μελέτες πίσω από τον μετρητή. Εκτελέστε ξανά τη μελέτη μετά την αναβάθμιση.") : raw)
        : (isGreek ? `Αδυναμία λήψης προσφοράς: ${raw}` : raw);
      setReportStatus((prev) => ({ ...prev, commercialOfferDownloading: false }));
      setCommercialOfferFeedback({ kind, error: message });
      return false;
    }
  };

  const handleDownloadTeaser = async () => {
    if (!currentProject?.id || reportStatus.teaserDownloading) return;
    if (blockStaleReportAction()) return;
    if (demoExportDisabledMessage) {
      setReportStatus((prev) => ({
        ...prev,
        error: `Error: ${demoExportDisabledMessage}`,
        success: "",
      }));
      return;
    }
    setReportStatus((prev) => ({
      ...prev,
      teaserDownloading: true,
      error: "",
      success: "",
    }));
    try {
      const { blob, filename } = await metenApi.downloadFeasibilityTeaser(
        String(currentProject.id)
      );
      triggerDownload(blob, filename || `feasibility_teaser_${currentProject.id}.html`);
      setReportStatus((prev) => ({
        ...prev,
        teaserDownloading: false,
        success: "Web teaser downloaded.",
      }));
    } catch (err) {
      const message = extractErrorMessage(err, "Failed to download web teaser.");
      const cleanMessage = message.toLowerCase().includes("teaser")
        ? "Error: Web teaser is not available for this run (re-run the simulation)"
        : `Error: ${message}`;
      setReportStatus((prev) => ({
        ...prev,
        teaserDownloading: false,
        error: cleanMessage,
      }));
    }
  };

  const handlePublishTeaser = async () => {
    if (!currentProject?.id || teaserPublish.loading) return;
    if (blockStaleReportAction()) return;
    setTeaserPublish((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const res = await metenApi.publishFeasibilityTeaser(String(currentProject.id));
      const url = res?.public_url || "";
      const expiresAt = res?.expires_at || "";
      let copied = false;
      if (url && navigator?.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
          copied = true;
        } catch (_) {
          // clipboard may be unavailable; the link is still shown inline
        }
      }
      let views = 0;
      try {
        const stats = await metenApi.getFeasibilityTeaserStats(String(currentProject.id));
        views = stats?.views || 0;
      } catch (_) {
        // stats are best-effort
      }
      setTeaserPublish({ loading: false, url, views, copied, expiresAt, error: "" });
    } catch (err) {
      const message = extractErrorMessage(err, "Failed to publish teaser.");
      setTeaserPublish((prev) => ({ ...prev, loading: false, error: message }));
    }
  };

  const handleDownloadDetailedTeaser = async () => {
    if (!currentProject?.id || reportStatus.detailedTeaserDownloading) return;
    if (blockStaleReportAction()) return;
    if (demoExportDisabledMessage) {
      setReportStatus((prev) => ({
        ...prev,
        error: `Error: ${demoExportDisabledMessage}`,
        success: "",
      }));
      return;
    }
    setReportStatus((prev) => ({
      ...prev,
      detailedTeaserDownloading: true,
      error: "",
      success: "",
    }));
    try {
      const { blob, filename } = await metenApi.downloadFeasibilityTeaser(
        String(currentProject.id),
        { kind: "detailed" }
      );
      triggerDownload(
        blob,
        filename || `feasibility_teaser_detailed_${currentProject.id}.html`
      );
      setReportStatus((prev) => ({
        ...prev,
        detailedTeaserDownloading: false,
        success: "Detailed teaser downloaded.",
      }));
    } catch (err) {
      const message = extractErrorMessage(err, "Failed to download detailed teaser.");
      const cleanMessage = message.toLowerCase().includes("teaser")
        ? "Error: Detailed teaser is not available for this run (re-run the simulation after this deploy)"
        : `Error: ${message}`;
      setReportStatus((prev) => ({
        ...prev,
        detailedTeaserDownloading: false,
        error: cleanMessage,
      }));
    }
  };

  const handlePublishDetailedTeaser = async () => {
    if (!currentProject?.id || detailedTeaserPublish.loading) return;
    if (blockStaleReportAction()) return;
    setDetailedTeaserPublish((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const res = await metenApi.publishFeasibilityTeaser(String(currentProject.id), {
        kind: "detailed",
      });
      const url = res?.public_url || "";
      const expiresAt = res?.expires_at || "";
      let copied = false;
      if (url && navigator?.clipboard?.writeText) {
        try {
          await navigator.clipboard.writeText(url);
          copied = true;
        } catch (_) {
          // clipboard may be unavailable; the link is still shown inline
        }
      }
      let views = 0;
      try {
        const stats = await metenApi.getFeasibilityTeaserStats(String(currentProject.id), {
          kind: "detailed",
        });
        views = stats?.views || 0;
      } catch (_) {
        // stats are best-effort
      }
      setDetailedTeaserPublish({ loading: false, url, views, copied, expiresAt, error: "" });
    } catch (err) {
      const message = extractErrorMessage(err, "Failed to publish detailed teaser.");
      setDetailedTeaserPublish((prev) => ({ ...prev, loading: false, error: message }));
    }
  };

  const handleRevokeTeaser = async () => {
    if (!currentProject?.id || teaserPublish.revoking) return;
    setTeaserPublish((prev) => ({ ...prev, revoking: true, error: "" }));
    try {
      await metenApi.revokeFeasibilityTeaser(String(currentProject.id));
      setTeaserPublish({
        loading: false,
        url: "",
        views: 0,
        copied: false,
        revoking: false,
        revoked: true,
        error: "",
      });
      // Backend revocation is project-wide: it disables the detailed link too.
      setDetailedTeaserPublish({
        loading: false,
        url: "",
        views: 0,
        copied: false,
        revoked: true,
        error: "",
      });
    } catch (err) {
      const message = extractErrorMessage(err, "Failed to revoke teaser link.");
      setTeaserPublish((prev) => ({ ...prev, revoking: false, error: message }));
    }
  };

  const handleGenerateReport = async () => {
    if (!currentProject?.id || reportStatus.generating) return;
    if (blockStaleReportAction()) return;
    if (demoExportDisabledMessage) {
      setReportStatus({
        proposalDownloading: false,
        reportDownloading: false,
        generating: false,
        generated: false,
        error: `Error: ${demoExportDisabledMessage}`,
        success: "",
      });
      return;
    }
    setReportStatus({
      proposalDownloading: false,
      reportDownloading: false,
      generating: true,
      generated: false,
      error: "",
      success: "",
    });
    try {
      await metenApi.generateFeasibilityReport(String(currentProject.id), {
        config: { ...finalPayloadConfig, reportBranding: portfolio?.project?.reportBranding || {} },
      });
      setReportStatus({
        proposalDownloading: false,
        reportDownloading: false,
        generating: false,
        generated: true,
        error: "",
        success: "Report generated successfully.",
      });
    } catch (err) {
      const message = extractErrorMessage(err, "Failed to generate report.");
      setReportStatus({
        proposalDownloading: false,
        reportDownloading: false,
        generating: false,
        generated: false,
        error: `Error: ${message}`,
        success: "",
      });
    }
  };

  const handleDownloadReport = async () => {
    if (!currentProject?.id || !reportStatus.generated || reportStatus.reportDownloading)
      return;
    if (blockStaleReportAction()) return;
    if (demoExportDisabledMessage) {
      setReportStatus((prev) => ({
        ...prev,
        proposalDownloading: false,
        reportDownloading: false,
        error: `Error: ${demoExportDisabledMessage}`,
        success: "",
      }));
      return;
    }
    setReportStatus((prev) => ({
      ...prev,
      proposalDownloading: false,
      reportDownloading: true,
      error: "",
      success: "",
    }));
    try {
      const { blob, filename } = await metenApi.downloadFeasibilityReport(
        String(currentProject.id)
      );
      triggerDownload(blob, filename || `feasibility_report_${currentProject.id}.pdf`);
      setReportStatus((prev) => ({
        ...prev,
        proposalDownloading: false,
        reportDownloading: false,
        success: "Report downloaded.",
      }));
    } catch (err) {
      const message = extractErrorMessage(err, "Failed to download report.");
      setReportStatus((prev) => ({
        ...prev,
        proposalDownloading: false,
        reportDownloading: false,
        error: `Error: ${message}`,
      }));
    }
  };

  const handleRunSimulation = async () => {
    if (!currentProject?.id || runStatus.loading) return;
    if (feasibilityDemoRestrictionMessage) {
      setRunStatus((prev) => ({
        ...prev,
        loading: false,
        error: feasibilityDemoRestrictionMessage,
        success: "",
      }));
      updateSimulationStatus({ status: "Not started", progress: 0 });
      return;
    }
    if (cfg.feasibility?.pvRisk?.enabled && !pvRiskReady) {
      setRunStatus(prev => ({ ...prev, error: isGreek ? "Η ανάλυση ΦΒ δεν έχει επιβεβαιωθεί ως συμβατή. Ελέγξτε την επιλογή μεταβλητότητας παραγωγής." : "PV analysis compatibility has not been confirmed. Check the PV variability option." }));
      return;
    }
    if (preRunBlockerList.length) {
      setRunStatus((prev) => ({ ...prev, loading: false, error: preRunBlockerList.map((b) => b.text).join(" "), success: "" }));
      return;
    }
    if (feasibilityBlockingErrors.length) {
      setRunStatus((prev) => ({
        ...prev,
        loading: false,
        error: feasibilityBlockingErrorText,
        success: "",
      }));
      updateSimulationStatus({ status: "Not started", progress: 0 });
      return;
    }
    setReportStatus({
      proposalDownloading: false,
      reportDownloading: false,
      generating: false,
      generated: false,
      error: "",
      success: "",
    });
    // `status: "pending"` from the very first frame, not after the POST comes
    // back. Dropping it left the alert below with nothing to render for the
    // whole round trip: the previous run's green line vanished, everything
    // under it rose by a row, and the blue "Queued" line pushed it back down
    // when the response arrived. That gap is the hitch on pressing Execute,
    // and it lasts exactly as long as the network does.
    awaitingRunAckRef.current = true;
    cancelRequestedRef.current = false;
    setRunStatus({ loading: true, status: "pending", error: "", success: "" });
    // The panel below moves in the same frame as the alert above it. It used to
    // wait for the POST as well, so the two halves of one answer arrived a
    // round trip apart -- which reads as the page correcting itself. A failure
    // overwrites this with "Failed" in the catch below.
    updateSimulationStatus({ status: "Queued", progress: 0 });
    activeRunFingerprintRef.current = currentRunFingerprint;
    try {
      // The scene shading is calculated beforehand (layout page or the notice
      // above Execute) and saved with the project; the run takes it from
      // there. Nothing is worked out here in the browser before the request
      // goes, so leaving the page once the run is sent cannot lose it. Without
      // it the run goes only when the user chose to run without it.
      const savedConfig = finalPayloadConfig;
      const withoutShading = runWithoutShadingRef.current;
      runWithoutShadingRef.current = false;
      if (sceneShadingPending(savedConfig).length && !withoutShading) {
        throw new Error(sceneSimulationIssues(savedConfig, isGreek).slice(-1)[0]);
      }
      let preparedConfig = withoutShading ? configWithoutLayoutShading(savedConfig) : savedConfig;
      if (savedConfig.pvDetailedShading?.enabled === true) {
        const module = await getModuleDetails(savedConfig.read_module, accessToken);
        preparedConfig = await prepareSceneSimulation(savedConfig, { module, projectId: currentProject.id });
      }
      updateSimulationStatus({ status: "Queued", phase: "queued", progress: 0, message: "" });
      const runResp = await metenApi.runFeasibilitySimulation({
        project_id: String(currentProject.id),
        start: formatDateIso(referenceYearStartDate),
        end: formatDateIso(referenceYearEndDate),
        config: { ...preparedConfig, reportBranding: portfolio?.project?.reportBranding || {} },
      });
      awaitingRunAckRef.current = false;
      const feasibilityId =
        runResp?.feasibility_id || runResp?.id || runResp?.feasibilityId;
      setRunStatus({
        loading: true,
        status: "pending",
        error: "",
        success: "Simulation triggered successfully.",
        runTime: "",
      });
      // The run is accepted at this line and nowhere earlier: the POST has
      // returned. Confirming on the click instead would promise something the
      // server had not yet agreed to.
      setTriggerToastOpen(true);
      try {
        const snapshot = await metenApi.getFeasibilityStatus(String(currentProject.id));
        if (snapshot?.last_run_at) {
          setSimulationStatus((prev) => ({
            ...prev,
            lastRun: snapshot.last_run_at,
          }));
        }
      } catch (err) {
        // ignore status refresh errors
      }
      if (!feasibilityId) {
      setRunStatus({
        loading: false,
        status: "pending",
        error: "Missing feasibility_id in response.",
        success: "",
        runTime: "",
      });
        updateSimulationStatus({ status: "Failed", progress: 0 });
        return;
      }
      await openFeasibilityStream(feasibilityId);
    } catch (err) {
      if (cancelRequestedRef.current) {
        awaitingRunAckRef.current = false;
        setRunStatus({ loading: false, cancelling: false, status: "cancelled", error: "", success: "" });
        updateSimulationStatus({ status: "Cancelled", phase: "", progress: 0, message: "Cancelled by user." });
        return;
      }
      if (err?.status === 403) {
        void refreshDemoCounters();
      }
      setRunStatus((prev) => ({
        ...prev,
        loading: false,
        error: extractErrorMessage(err, "Failed to start simulation."),
        success: "",
      }));
      awaitingRunAckRef.current = false;
      updateSimulationStatus({ status: "Failed", progress: 0 });
    }
  };
  const handleExecuteClick = async () => {
    if (savingBeforeRun) return;
    if (history.isDirty) {
      // Execute saves the project first and then runs, instead of sending the
      // user to the File menu. The run reads what was just saved, so the
      // configuration it uses is the one on screen. When the save cannot go
      // through (never saved, or a tab still holds an unsaved draft) the
      // dialog says why and nothing runs.
      if (!currentProject?.id || typeof saveProject !== "function") {
        runWithoutShadingRef.current = false;
        setUnsavedSaveError("");
        setUnsavedDialogOpen(true);
        return;
      }
      setSavingBeforeRun(true);
      try {
        await saveProject();
      } catch (err) {
        runWithoutShadingRef.current = false;
        setUnsavedSaveError(String(err?.message || err || ""));
        setUnsavedDialogOpen(true);
        return;
      } finally {
        setSavingBeforeRun(false);
      }
    }
    if (preRunWarningList.length && acceptedPreRunWarnings() !== warningsKey(preRunWarningList)) {
      setPreRunWarningsOpen(true);
      return;
    }
    // The notice below Execute already offers both choices. Bring it into
    // view instead of opening a second copy of the same decision.
    if (shadingPending.length && !runWithoutShadingRef.current) {
      shadingNoticeRef.current?.scrollIntoView?.({ behavior: "smooth", block: "center" });
      shadingNoticeRef.current?.focus?.();
      return;
    }
    handleRunSimulation();
  };
  // The quick study's «Δημιουργία και εκτέλεση», step 3 (components/quickStudyRun.js):
  // Execute, as the button does, and the results card when the study is done.
  // Anything that stops the run (a check before it, a failure) ends the quick
  // flow here, with its message on this page.
  const quickRunStep = quickStep(typeof window === "undefined" ? null : window.location) === "run";
  const quickRunRef = useRef("idle"); // idle → started → running → done
  // The latest Execute and checks, not the ones of the render that set the timer.
  const quickLatestRef = useRef({});
  quickLatestRef.current = { execute: handleExecuteClick, blockers: preRunBlockerList };
  useEffect(() => {
    if (!quickRunStep || quickRunRef.current !== "idle" || !currentProject?.id || runStatus.loading) return undefined;
    const timer = setTimeout(() => {
      if (quickRunRef.current !== "idle") return;
      if (quickStep(window.location) !== "run") return;  // stopped meanwhile
      quickRunRef.current = "started";
      if (quickLatestRef.current.blockers.length) {
        quickRunRef.current = "done";
        navigate(withoutQuick(window.location), { replace: true });
        return;
      }
      quickLatestRef.current.execute();
    }, 1500);
    return () => clearTimeout(timer);
  }, [quickRunStep, currentProject?.id, runStatus.loading]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!quickRunStep) return;
    const state = quickRunRef.current;
    if (state === "started" && runStatus.loading) quickRunRef.current = "running";
    if (quickRunRef.current !== "running" || runStatus.loading) return;
    quickRunRef.current = "done";
    if (runStatus.status === "success") setStudyResultsOpen(true);
    navigate(withoutQuick(window.location), { replace: true });
  }, [quickRunStep, runStatus.loading, runStatus.status, navigate]);
  // The warnings dialog or the unsaved dialog answered "no": the run did not
  // start, so the quick flow ends and the user carries on by hand.
  useEffect(() => {
    if (!quickRunStep || quickRunRef.current !== "started") return;
    if (preRunWarningsOpen || unsavedDialogOpen) return;
    const timer = setTimeout(() => {
      if (quickRunRef.current === "started" && !runStatus.loading && !savingBeforeRun) {
        quickRunRef.current = "done";
        navigate(withoutQuick(window.location), { replace: true });
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [quickRunStep, preRunWarningsOpen, unsavedDialogOpen, runStatus.loading, savingBeforeRun, navigate]);

  const runWithoutLayoutShading = () => {
    runWithoutShadingRef.current = true;
    handleExecuteClick();
  };
  const handleCancelSimulation = async () => {
    if (!currentProject?.id || runStatus.cancelling) return;
    cancelRequestedRef.current = true;
    setRunStatus((prev) => ({ ...prev, cancelling: true, error: "" }));
    updateSimulationStatus({ message: "Cancelling…" });
    if (preparingSceneRef.current) return;
    try {
      await metenApi.cancelFeasibilitySimulation({
        project_id: String(currentProject.id),
      });
    } catch (err) {
      setRunStatus((prev) => ({
        ...prev,
        cancelling: false,
        error: err?.responseText || err?.message || "Failed to cancel the run.",
      }));
    }
  };

  useEffect(() => {
    if (!currentProject?.id) {
      activeRunFingerprintRef.current = "";
      setCompletedRunFingerprint("");
      return;
    }
    activeRunFingerprintRef.current = "";
    setCompletedRunFingerprint(readCompletedRunFingerprint(String(currentProject.id)));
  }, [currentProject?.id]);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;
    // Returning to this page mid-run is answered only by this read. When it
    // failed, the error was logged and nothing else happened: the page kept
    // its initial state (Not started, 0 %, Execute enabled) while the run went
    // on, and the poll never started because it only runs for a run the page
    // knows about. It is retried, and a final failure is shown.
    const STATUS_RETRY_DELAYS_MS = [1500, 3000, 6000];
    const loadStatus = async (attempt = 0) => {
      if (!isSessionReady) {
        setStatusLoading(true);
        return;
      }
      if (!currentProject?.id) {
        setStatusLoading(false);
        return;
      }
      let retrying = false;
      try {
        setStatusLoading(true);
        const snapshot = await metenApi.getFeasibilityStatus(String(currentProject.id));
        if (cancelled || preparingSceneRef.current || awaitingRunAckRef.current) return;
        setStatusLoadError(false);
        const normalized = normalizeRunStatus(snapshot?.status);
        const snapshotProgress = Number.isFinite(Number(snapshot?.progress))
          ? Number(snapshot?.progress)
          : null;
        const snapshotPhase = snapshot?.phase || "";
        const snapshotRawMessage =
          snapshot?.message ||
          (normalized === "failed" ? snapshot?.error_message || "Simulation failed." : "");
        const snapshotMessage =
          String(snapshotRawMessage || "").trim().toLowerCase() === "completed"
            ? "Results are available."
            : formatStreamMessage(snapshotRawMessage);
        setRunStatus((prev) => ({
          ...prev,
          loading: normalized === "pending" || normalized === "running",
          status: normalized === "not_started" ? "" : normalized,
          error:
            normalized === "failed"
              ? "Simulation failed. Check message field for more details."
              : "",
          success: normalized === "success" ? "Simulation completed successfully." : "",
          runTime: snapshot?.run_time || "",
        }));
        // Reopening a project: recover the stored run's weather provenance so
        // the page reports the source that produced the results on screen.
        setRunPvRisk(snapshot?.pv_financial_risk || snapshot?.summary?.pv_financial_risk || null);
        if (snapshot?.weather_source) {
          setRunWeatherSource(snapshot.weather_source);
        }
        setSimulationStatus((prev) => ({
          ...prev,
          status: mapStatusLabel(normalized),
          progress: snapshotProgress ?? 0,
          phase: snapshotPhase || "",
          message: snapshotMessage || "",
          lastRun: snapshot?.last_run_at || prev.lastRun,
          reportsAvailable: Boolean(snapshot?.reports_available || normalized === "success"),
          reportsFromLastCompleted: Boolean(snapshot?.reports_from_last_completed),
          lastCompletedAt: snapshot?.last_completed_at || null,
        }));
        setReportStatus((prev) => ({
          ...prev,
        proposalDownloading: false,
        reportDownloading: false,
        generated: false,
        }));
        if (snapshot?.reports_available) {
          const snapshotFingerprint = getSnapshotRunFingerprint(snapshot);
          if (snapshotFingerprint) {
            markCompletedRunFingerprint(snapshotFingerprint);
          }
        }
        if (normalized === "success") {
          void refreshDemoCounters();
        }
        if (
          (normalized === "pending" || normalized === "running") &&
          snapshot?.feasibility_id
        ) {
          await openFeasibilityStream(snapshot.feasibility_id);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("Failed to load feasibility status.", err);
        if (attempt < STATUS_RETRY_DELAYS_MS.length) {
          retrying = true;
          retryTimer = window.setTimeout(
            () => loadStatus(attempt + 1),
            STATUS_RETRY_DELAYS_MS[attempt]
          );
        } else {
          setStatusLoadError(true);
        }
      } finally {
        if (!cancelled && !retrying) setStatusLoading(false);
      }
    };
    setStatusLoadError(false);
    loadStatus();
    return () => {
      cancelled = true;
      if (retryTimer) window.clearTimeout(retryTimer);
    };
  }, [
    currentProject?.id,
    isSessionReady,
    markCompletedRunFingerprint,
    openFeasibilityStream,
    refreshDemoCounters,
  ]);

  // Live progress fallback: while a run is active, poll the status snapshot every
  // few seconds so the panel updates without a manual refresh. The SSE stream is
  // best-effort; this guarantees progress/phase/status stay current. Mirrors the
  // on-mount snapshot mapping above and stops once the run reaches a terminal state.
  // THE FEEDBACK LOOP THIS SHAPE EXISTS TO BREAK. This effect used to list
  // `simulationStatus.status` and `runStatus.loading` among its dependencies
  // and then WRITE both of them. Every answer it got tore the effect down,
  // rebuilt it and fired `poll()` again immediately -- so a run produced a
  // burst of polls, each racing the SSE stream for the same two pieces of
  // state, and the page showed Queued, then nothing, then Running again, in
  // whatever order the answers happened to land. Whether a run is active is
  // now read from a ref at tick time instead of captured as a dependency, so
  // the interval is created once per project and nothing it writes can
  // restart it.
  const runActiveRef = useRef(false);
  useEffect(() => {
    runActiveRef.current = runInFlight;
  }, [runInFlight]);

  useEffect(() => {
    if (!isSessionReady || !currentProject?.id) return undefined;

    let cancelled = false;
    const poll = async () => {
      if (!runActiveRef.current || preparingSceneRef.current) return;
      try {
        const requestedAt = Date.now();
        const snapshot = await metenApi.getFeasibilityStatus(String(currentProject.id));
        if (cancelled || preparingSceneRef.current || lastStreamUpdateRef.current > requestedAt) return;
        const normalized = normalizeRunStatus(snapshot?.status);
        const snapshotProgress = Number.isFinite(Number(snapshot?.progress))
          ? Number(snapshot?.progress)
          : null;
        const rawMessage =
          snapshot?.message ||
          (normalized === "failed" ? snapshot?.error_message || "Simulation failed." : "");
        const message =
          String(rawMessage || "").trim().toLowerCase() === "completed"
            ? "Results are available."
            : formatStreamMessage(rawMessage);
        // THE RACE THIS GUARD EXISTS FOR. This effect re-runs the instant
        // `runStatus.loading` flips, and `poll()` is called immediately -- so
        // the first poll of a run usually reaches the server BEFORE the run
        // does. The snapshot it gets back is the previous one, `not_started`,
        // which the mapping below turns into `status: ""` and `loading: false`:
        // the status line vanishes, the button un-disables, and both come back
        // a second later when the next poll finally sees the run. That is the
        // flicker on pressing Execute. A `not_started` answer cannot describe a
        // run we have already launched, so until the server says anything of
        // its own, it is discarded rather than believed.
        if (awaitingRunAckRef.current) return;
        awaitingRunAckRef.current = false;
        setSimulationStatus((prev) => ({
          ...prev,
          status: mapStatusLabel(normalized),
          progress: snapshotProgress ?? prev.progress,
          phase: snapshot?.phase || prev.phase,
          message: message || prev.message,
          lastRun: snapshot?.last_run_at || prev.lastRun,
          reportsAvailable: Boolean(snapshot?.reports_available || normalized === "success"),
          reportsFromLastCompleted: Boolean(snapshot?.reports_from_last_completed),
          lastCompletedAt: snapshot?.last_completed_at || null,
        }));
        setRunStatus((prev) => ({
          ...prev,
          loading: normalized === "pending" || normalized === "running",
          cancelling:
            normalized === "pending" || normalized === "running"
              ? prev.cancelling
              : false,
          status: normalized === "not_started" ? "" : normalized,
          success:
            normalized === "success" ? "Simulation completed successfully." : "",
          error: normalized === "failed" ? "Simulation failed. Check message field for more details." : "",
          runTime: snapshot?.run_time || prev.runTime,
        }));
        if (normalized === "success") {
          // Record the completed-run fingerprint so the report/download buttons
          // unlock. The SSE stream normally does this, but it is best-effort and
          // may not deliver the terminal event; the launch-time fingerprint is the
          // exact value the gate compares against, with the snapshot as fallback.
          const completedFingerprint =
            activeRunFingerprintRef.current || getSnapshotRunFingerprint(snapshot);
          if (completedFingerprint) markCompletedRunFingerprint(completedFingerprint);
        }
      } catch (err) {
        if (!cancelled) console.error("Feasibility status poll failed.", err);
      }
    };
    // No immediate first call. It was fired the moment Execute was pressed and
    // therefore usually reached the server BEFORE the run did, coming back
    // with the previous state and undoing what the click had just set. The
    // first tick is 2.5 s later, by which time the answer means something; the
    // on-mount snapshot above already covers a page opened mid-run.
    const intervalId = window.setInterval(poll, 2500);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [isSessionReady, currentProject?.id, markCompletedRunFingerprint]);

  useEffect(() => {
    if (!isSessionReady || currentProject?.id) return;
    setStatusLoading(false);
    setSimulationStatus({
      status: "Not started",
      progress: 0,
      phase: "",
      message: "",
      lastRun: null,
      lastUpdated: null,
      reportsAvailable: false,
      reportsFromLastCompleted: false,
      lastCompletedAt: null,
    });
    setRunStatus({
      loading: false,
      status: "",
      error: "",
      success: "",
      runTime: "",
    });
    setReportStatus({
      generating: false,
      generated: false,
      error: "",
      success: "",
    });
  }, [currentProject?.id, isSessionReady]);

  useEffect(() => {
    if (!reportStatus.error && !reportStatus.success) return;
    const timer = setTimeout(() => {
      setReportStatus((prev) => ({ ...prev, error: "", success: "" }));
    }, 5000);
    return () => clearTimeout(timer);
  }, [reportStatus.error, reportStatus.success]);

  // A cancellation that is never acknowledged must not leave the button spinning
  // for the rest of the session. `cancelling` is set on the click and cleared
  // only by the poller, when it sees the run reach a terminal state -- so if the
  // poll stops, the stream dies, or the worker never notices the flag, nothing
  // ever clears it and the only way out is a reload. After this long the
  // request has either taken effect or is not going to, and the user gets the
  // button back rather than a control that has stopped answering.
  useEffect(() => {
    if (!runStatus.cancelling) return undefined;
    const timer = window.setTimeout(() => {
      setRunStatus((prev) => (prev.cancelling ? { ...prev, cancelling: false } : prev));
    }, CANCEL_ACK_TIMEOUT_MS);
    return () => window.clearTimeout(timer);
  }, [runStatus.cancelling]);

  useEffect(() => {
    if (!triggerToastOpen) return undefined;
    const timer = setTimeout(() => setTriggerToastOpen(false), 4000);
    return () => clearTimeout(timer);
  }, [triggerToastOpen]);

  useEffect(() => {
    return () => {
      if (streamRef.current && typeof streamRef.current.close === "function") {
        streamRef.current.close();
      }
      streamActiveRef.current = false;
      streamHasPayloadRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!runStatus.status) return;
    if (streamActiveRef.current) return;
    if (streamHasPayloadRef.current) return;
    if (Date.now() - lastStreamUpdateRef.current < 500) return;
    if (runStatus.status === "pending") {
      updateSimulationStatus({ status: "Queued", progress: 0 });
      return;
    }
    if (runStatus.status === "running") {
      updateSimulationStatus({ status: "Running" });
      return;
    }
    if (runStatus.status === "success") {
      updateSimulationStatus({ status: "Finished" });
      return;
    }
    if (runStatus.status === "failed") {
      updateSimulationStatus({ status: "Failed" });
    }
  }, [runStatus.status, runStatus.runTime]);

  useEffect(() => {
    if (runStatus.status !== "running") {
      lastRunRefreshRef.current = false;
      return;
    }
    if (!currentProject?.id) return;
    if (lastRunRefreshRef.current) return;
    lastRunRefreshRef.current = true;
    let cancelled = false;
    const refreshLastRun = async () => {
      try {
        const snapshot = await metenApi.getFeasibilityStatus(String(currentProject.id));
        if (cancelled) return;
        if (snapshot?.last_run_at) {
          setSimulationStatus((prev) => ({
            ...prev,
            lastRun: snapshot.last_run_at,
          }));
        }
      } catch (err) {
        // ignore refresh errors
      }
    };
    refreshLastRun();
    return () => {
      cancelled = true;
    };
  }, [runStatus.status, currentProject?.id]);

  return (
    <FeasibilityShell
      compact
      maxWidth="none"
      manual={{ settlementType: cfg?.settlementType, section: "simulation" }}
      title={isGreek ? "Προσομοίωση & αναφορές" : "Simulation & reports"}
      subtitle={isGreek ? "Ελέγξτε τις ρυθμίσεις, επιλέξτε την ανάλυση κινδύνου και εκτελέστε τη μελέτη." : "Review the settings, select the risk analysis and run the study."}
      headerAside={
        demoProfile?.demoModeEnabled ? (
          <Alert
            severity={feasibilityDemoRestrictionMessage ? "warning" : "info"}
            sx={{
              minWidth: { xs: "100%", lg: 320 },
              mb: 0,
              "& .MuiAlert-message": { width: "100%" },
            }}
          >
            {feasibilityDemoRestrictionMessage ? (
              feasibilityDemoRestrictionMessage
            ) : (
              <>
                <Box sx={{ fontWeight: 700 }}>Remaining feasibility runs today</Box>
                <Box>
                  {demoProfile.feasibilityRemainingToday} /{" "}
                  {demoProfile.demoFeasibilityLimitPerDay}
                </Box>
              </>
            )}
          </Alert>
        ) : null
      }
    >
      {/** Reports should be available only after successful simulation */}
      <Grid container spacing={1.25}>
        <Grid size={{ xs: 12 }}>
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, width: "100%" }}>
            <Stack spacing={1.25}>
              <Typography
                variant="subtitle1"
                component="h2"
                sx={{ color: "text.primary" }}
              >
                {isGreek ? "Ρυθμίσεις εκτέλεσης" : "Run settings"}
              </Typography>
              {/* One badge per generating asset the project actually has.
                  Weather source is stored per asset (is_active_pv /
                  is_active_wind), so a PV+wind project has two independent
                  selections. Reporting only the PV one meant a wind dataset could
                  be assigned on the Weather page and never appear here - looking
                  as though the choice had not taken effect. The "(PV)" qualifier
                  is dropped when there is nothing to disambiguate against. */}
              {simAssets.hasPv && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {simAssets.hasWind ? (isGreek ? "Πηγή καιρού (ΦΒ):" : "Weather source (PV):") : (isGreek ? "Πηγή καιρού:" : "Weather source:")}
                  </Typography>
                  <WeatherSourceBadge
                    projectId={currentProject?.id}
                    accessToken={accessToken}
                    asset="pv"
                    runWeatherSource={runWeatherSource}
                  />
                </Stack>
              )}
              {simAssets.hasWind && (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Typography variant="body2" sx={{ fontWeight: 700 }}>
                    {simAssets.hasPv ? (isGreek ? "Πηγή καιρού (Α/Γ):" : "Weather source (Wind):") : (isGreek ? "Πηγή καιρού:" : "Weather source:")}
                  </Typography>
                  {/* No runWeatherSource: the run summary's stamp comes from
                      household.weather_source, which is the PV path only
                      (BESS_feasibility/bess_simulation.py:1132). Passing it here
                      would report the PV dataset as the wind one. Without it the
                      badge shows the configured is_active_wind selection, which is
                      the honest answer until a wind run stamp exists. */}
                  <WeatherSourceBadge
                    projectId={currentProject?.id}
                    accessToken={accessToken}
                    asset="wind"
                  />
                </Stack>
              )}
              {/* Standalone BESS: no generating asset, so there is no weather
                  source to report. Said explicitly rather than just omitting the
                  row - an absent line reads as a missing value, and this page
                  previously showed "Weather source (PV): Platform internal" here,
                  which was worse: it named a source for an asset that does not
                  exist. Wording matches the Weather Data page. */}
              {!simAssets.hasPv && !simAssets.hasWind && (
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  No weather source is required — this project has no generating
                  asset (asset type is <b>{cfg?.systemType || "Standalone BESS"}</b>).
                </Typography>
              )}
              <Box component="details" sx={{ '& summary': { cursor: 'pointer', color: 'primary.main', fontWeight: 600 }, '& > p': { mt: 1 } }}>
                <summary>{isGreek ? "Πώς χρησιμοποιούνται το έτος αναφοράς και ο καιρός" : "How the reference year and weather are used"}</summary>
                {/* What the reference year actually decides. It used to be
                    described as the year the weather comes from, which is only
                    true of the platform's internal source: an imported or
                    fetched dataset supplies the weather itself and is mapped
                    onto whatever calendar this year sets, so a study on a 2024
                    TMY reading "Reference Year 2025" looked like a mismatch
                    nobody could resolve from the page. */}
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {isGreek
                    ? "Η μελέτη αναφοράς καλύπτει ολόκληρο το επιλεγμένο έτος με ωριαίο βήμα. Το έτος αναφοράς καθορίζει το ημερολόγιο και τις ωριαίες τιμές αγοράς. Η πολυετής οικονομική προβολή χρησιμοποιεί τις παραδοχές γήρανσης, τιμών και επανυπολογισμού λειτουργίας του έργου. Όταν ενεργοποιείτε τη μεταβλητότητα ΦΒ, εκτελούνται επιπλέον μελέτες με ιστορικά προφίλ παραγωγής."
                    : "The reference study covers the full selected year at hourly resolution. The reference year sets the calendar and hourly market prices. The lifetime financial projection uses the project's ageing, price and operating re-calculation assumptions. Enabling PV variability adds operating studies with historical production profiles."}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  {isGreek
                    ? "Ο καιρός προέρχεται από την πηγή που φαίνεται παραπάνω. Ένα εισαγόμενο ή ληφθέν σύνολο δεδομένων χρησιμοποιείται ανεξάρτητα από το έτος αναφοράς· μόνο η εσωτερική πηγή της πλατφόρμας χρησιμοποιεί τον καιρό του ίδιου έτους."
                    : "The weather comes from the source named above. An imported or fetched dataset supplies it directly, whatever the reference year; only the platform's internal source reads that year's own weather."}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                  <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {isGreek ? "Η χρονική ανάλυση είναι πάντα ωριαία." : "Resolution is always hourly and is not configurable."}
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", xl: "minmax(0, 1.15fr) minmax(420px, 0.85fr)" }, gap: 1.25, alignItems: "start" }}>
              <Grid container spacing={1.25} sx={{ maxWidth: 1080, gridColumn: 1, gridRow: 1 }}>
                {/* Four digits, and a two-digit field beside it: a third of the
                    row each made both look like they expected something longer.
                    The helper text under them is what needs the width, and it
                    wraps happily. */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={0.35} sx={{ mb: 0.5, minHeight: 38 }}>
                    <Typography component="label" htmlFor="feasibility-reference-year" sx={yearFieldLabelSx}>
                      {isGreek ? "Έτος αναφοράς" : "Reference year"}
                    </Typography>
                    <Tooltip title={isGreek
                      ? "Το ιστορικό έτος των τιμών αγοράς και του καιρού της πλατφόρμας. Αν έχει επιλεγεί εξωτερικό σύνολο καιρού ή TMY, χρησιμοποιείται εκείνο. Δεν αλλάζει την έναρξη λειτουργίας του έργου."
                      : "The historical year for market prices and platform weather. A selected external weather dataset or TMY replaces platform weather. This does not change the operating start year."}>
                      <IconButton size="small" sx={{ p: 0.35 }} aria-label={isGreek ? "Πληροφορίες: Έτος αναφοράς" : "Information: Reference year"}><InfoOutlinedIcon sx={{ fontSize: 18 }} /></IconButton>
                    </Tooltip>
                  </Stack>
                  <TextField
                    id="feasibility-reference-year"
                    select
                    SelectProps={{ inputProps: { "aria-label": isGreek ? "Έτος αναφοράς" : "Reference year" } }}
                    value={simYear}
                    onChange={(e) => {
                      const year = Number(e.target.value);
                      history.commit(
                        setPathInObject(
                          setPathInObject(setPathInObject(mergedCfg, "feasibility.operatingStartYear", operationYear), "feasibility.simulationYear", year),
                          "feasibility.simulationHorizonDays",
                          String(deriveHorizonDays(year))
                        )
                      );
                    }}
                    fullWidth
                    sx={fieldSx}
                  >
                    {availableYears.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                  {/* Whether the market actually has that year. A part-loaded
                      year runs to completion and simply earns less, which the
                      results page cannot tell apart from a weak project -- so
                      it is said here, before the run. Silent when the year is
                      complete, and silent when the check itself fails: this is
                      an aid, not a gate. */}
                  {/* `complete === null` means the market declares no timezone,
                      so the server could not build an honest denominator. Say
                      nothing rather than measure against a guess. */}
                  {coverage && !coverage.error && coverage.data && coverage.data.complete === false ? (
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.5, color: "warning.dark" }}
                    >
                      {/* The GAP, not a ratio. The denominator is the count of
                          wall-clock labels the price table can hold, which in a
                          daylight-saving market is 8,759 for a normal year, not
                          8,760: the March hour never happens and the repeated
                          October hour is one row under the table's unique key.
                          The study still runs 8,760 hourly steps -- the loader
                          lets the one stored October price stand for both
                          instants -- so printing "x / 8,759 hours of this year"
                          invited the reader to correct a number that was right,
                          about a quantity they were not being shown. The hours
                          missing is the same fact and needs no denominator. */}
                      <span>Market prices are missing for</span>
                      {` ${(coverage.data.expected_hours - coverage.data.priced_hours).toLocaleString("en-GB")} `}
                      <span>hours of the series</span>{` ${coverage.year} · ${coverage.zone}`}
                      <span>. Those hours earn nothing in the study.</span>
                    </Typography>
                  ) : null}
                  {coverage && !coverage.error && coverage.data?.complete
                    && coverage.data.settled_intervals === 0 ? (
                    <Typography
                      variant="caption"
                      sx={{ display: "block", mt: 0.5, color: "warning.dark" }}
                    >
                      This year is priced entirely from forecasts — no settled prices are stored for it.
                    </Typography>
                  ) : null}
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={0.35} sx={{ mb: 0.5, minHeight: 38 }}>
                    <Typography component="label" htmlFor="feasibility-operating-start-year" sx={yearFieldLabelSx}>
                      {isGreek ? "Έτος έναρξης λειτουργίας" : "Operating start year"}
                    </Typography>
                    <Tooltip title={isGreek
                      ? "Το πρώτο ημερολογιακό έτος λειτουργίας του έργου. Καθορίζει τις ημερομηνίες έναρξης/λήξης και τις χρονιές της οικονομικής προβολής. Η υποβάθμιση και οι ταμειακές ροές μετρώνται από το 1ο έτος λειτουργίας. Δεν αλλάζει τα δεδομένα καιρού ή τιμών αναφοράς."
                      : "The first calendar year of operation. Sets the project dates and financial projection calendar. Degradation and cash flows are counted from operating year 1. Does not change reference weather or market prices."}>
                      <IconButton size="small" sx={{ p: 0.35 }} aria-label={isGreek ? "Πληροφορίες: Έτος έναρξης λειτουργίας" : "Information: Operating start year"}><InfoOutlinedIcon sx={{ fontSize: 18 }} /></IconButton>
                    </Tooltip>
                  </Stack>
                  <TextField id="feasibility-operating-start-year"
                    value={mergedCfg.feasibility?.operatingStartYear ?? operationYear}
                    onChange={(e) => { if (/^\d{0,4}$/.test(e.target.value)) setFeasibility("operatingStartYear", e.target.value); }}
                    error={!validOperatingStartYear(operationYear)}
                    helperText={!validOperatingStartYear(operationYear) ? "1900–2200" : undefined}
                    inputProps={{ inputMode: "numeric", maxLength: 4, "aria-label": isGreek ? "Έτος έναρξης λειτουργίας" : "Operating start year" }}
                    fullWidth sx={fieldSx} />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Stack direction="row" alignItems="center" spacing={0.35} sx={{ mb: 0.5, minHeight: 38 }}>
                    <Typography component="label" htmlFor="field-sim-horizon" sx={yearFieldLabelSx}>
                      {isGreek ? "Διάρκεια έργου (έτη)" : "Project lifetime (years)"}
                    </Typography>
                    <Tooltip title={isGreek ? "Ο αριθμός των ετών λειτουργίας που εξετάζει η μελέτη, ξεκινώντας από το έτος έναρξης λειτουργίας." : "The number of operating years assessed, beginning with the operating start year."}>
                      <IconButton size="small" sx={{ p: 0.35 }} aria-label={isGreek ? "Πληροφορίες: Διάρκεια έργου" : "Information: Project lifetime"}><InfoOutlinedIcon sx={{ fontSize: 18 }} /></IconButton>
                    </Tooltip>
                  </Stack>
                  <TextField
                    id="field-sim-horizon"
                    value={mergedCfg.feasibility?.settlementPeriod ?? ""}
                    onChange={(e) => {
                      const raw = e.target.value.replace(",", ".");
                      if (!/^\d*\.?\d*$/.test(raw)) return;
                      setFeasibility("settlementPeriod", raw);
                    }}
                    inputProps={{ "aria-label": isGreek ? "Διάρκεια έργου (έτη)" : "Project lifetime (years)" }}
                    fullWidth
                    sx={fieldSx}
                  />
                  {/* The rate is set on Technical Assumptions; the horizon is
                      the field above. They multiply out into every year of the
                      financial projection, and neither page showed the other,
                      so a curve that stops short of the study was invisible. */}
                  {degradation.text ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
                      <span>{isGreek ? "Υποβάθμιση:" : "Degradation:"}</span>{` ${degradation.text}`}
                    </Typography>
                  ) : null}
                  {degradation.mismatch === "shortCurve" ? (
                    <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "warning.dark" }}>
                      <span>The degradation curve stops at year</span>{` ${degradation.curveYears}`}
                      <span>, before the study ends at year</span>{` ${degradation.studyYears}`}
                      <span>. The remaining years are extrapolated.</span>
                    </Typography>
                  ) : degradation.mismatch === "noDegradation" ? (
                    <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "warning.dark" }}>
                      No degradation is set, so the study holds output flat for its whole life.
                    </Typography>
                  ) : null}
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Box sx={projectDateSx}>
                      <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontWeight: 700 }}>
                        {isGreek ? "Έναρξη έργου" : "Project start"}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {projectPeriod.start ? formatDate(projectPeriod.start) : "—"}
                      </Typography>
                    </Box>
                    <Box sx={projectDateSx}>
                      <Typography variant="caption" sx={{ display: "block", color: "text.secondary", fontWeight: 700 }}>
                        {isGreek ? "Λήξη έργου" : "Project end"}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {projectPeriod.end ? formatDate(projectPeriod.end) : "—"}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
                    {isGreek ? "Η διάρκεια μετρά από το έτος έναρξης λειτουργίας, ανεξάρτητα από το έτος αναφοράς δεδομένων." : "The lifetime starts at the operating start year, independently of the reference data year."}
                  </Typography>
                </Grid>
              </Grid>
              {(deriveProjectAssets(cfg).hasPv || cfg.feasibility?.pvRisk?.enabled || runPvRisk) && <Box sx={{ gridColumn: "1 / -1", gridRow: { xs: 3, xl: 2 } }}><PvFinancialRisk
                config={finalPayloadConfig} projectId={currentProject?.id} accessToken={accessToken}
                disabled={runInFlight} onReady={setPvRiskReady} result={runPvRisk}
                onChange={value => history.commit({ ...cfg, feasibility: { ...cfg.feasibility, pvRisk: value } })} /></Box>}
              {/* What is about to run, in one line each. The scope of a study
                  is spread across Asset Setup, Market Data, Weather Data and
                  this page; the only thing visible here before was the weather
                  source, so a run against the wrong market stack was found out
                  from the revenue rather than from the page. Every value is
                  read from the payload this button will post -- not from the
                  stored project -- so it describes the run, not the last save. */}
              <Box
                sx={{
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  backgroundColor: "action.hover",
                  gridColumn: { xs: 1, xl: 2 },
                  gridRow: { xs: 2, xl: 1 },
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  {isGreek ? "Τι περιλαμβάνει η εκτέλεση" : "What this run covers"}
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    maxWidth: 900,
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                    columnGap: 1.5,
                    rowGap: 0.5,
                  }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ gridColumn: { sm: "1 / -1" } }}>
                    <span>{isGreek ? "Τύπος μονάδας:" : "Asset type:"}</span>{" "}
                    <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                      {runScope.systemType || "—"}
                    </Box>
                  </Typography>
                  {runScope.pvKw ? (
                    <Typography variant="body2" color="text.secondary">
                      <span>{isGreek ? "Εγκατεστημένα ΦΒ:" : "PV installed:"}</span>{" "}
                      <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                        {`${formatScopeKw(runScope.pvKw)} kWp`}
                      </Box>
                    </Typography>
                  ) : null}
                  {runScope.bessMw ? (
                    <Typography variant="body2" color="text.secondary">
                      <span>{isGreek ? "Μπαταρία:" : "Battery:"}</span>{" "}
                      <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                        {`${formatScopePower(runScope.bessKw, "W")} / ${formatScopePower(runScope.bessKwh, "Wh")}`}
                      </Box>
                    </Typography>
                  ) : null}
                  <Typography variant="body2" color="text.secondary" sx={{ gridColumn: { sm: "1 / -1" } }}>
                    <span>{isGreek ? "Ζώνη αγοράς:" : "Bidding zone:"}</span>{" "}
                    <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                      {runScope.biddingZone || "—"}
                    </Box>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ gridColumn: { sm: "1 / -1" } }}>
                    {runIsBtm ? <span>{isGreek ? "Αποτίμηση με:" : "Valued at:"}</span> : <span>{isGreek ? "Αγορές αποτίμησης:" : "Markets priced:"}</span>}{" "}
                    <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                      {runIsBtm
                        ? btmValuation.map((label, index) => (
                            <Fragment key={label}>
                              {index ? " · " : null}
                              <span>{label}</span>
                            </Fragment>
                          ))
                        : activeMarkets.length ? activeMarkets.join(" · ") : "—"}
                    </Box>
                  </Typography>
                </Box>
                {!runIsBtm && !activeMarkets.length ? (
                  <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "warning.dark" }}>
                    No revenue stream is included, so the study will value the plant at nothing. Choose the markets on the Market Data page.
                  </Typography>
                ) : null}
              </Box>
              </Box>
              <Stack direction="row" justifyContent="flex-start" alignItems="center" sx={{ pt: 1 }}>
                <Box
                  ref={buttonRowRef}
                  sx={{
                    display: "inline-flex",
                    cursor: !currentProject?.id ? "not-allowed" : "pointer",
                  }}
                  title={
                    !currentProject?.id
                      ? "Load a project to perform the simulation."
                      : undefined
                  }
                >
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    disabled={
                      !currentProject?.id ||
                      runInFlight ||
                      savingBeforeRun ||
                      Boolean(preRunBlockerList.length) ||
                      Boolean(feasibilityDemoRestrictionMessage) ||
                      Boolean(feasibilityBlockingErrors.length) ||
                      Boolean(cfg.feasibility?.pvRisk?.enabled && !pvRiskReady)
                    }
                    onClick={handleExecuteClick}
                    sx={{
                      pointerEvents:
                        !currentProject?.id || feasibilityBlockingErrors.length || preRunBlockerList.length
                          ? "none"
                          : "auto",
                    }}
                    // The label never changes and the spinner's slot is always
                    // there, so the box is the same width in both states -- the
                    // success toast is anchored to this button and jumped when
                    // it resized. On the RIGHT: reserved space after the text
                    // reads as padding, while the same gap before it read as a
                    // missing icon even when nothing was running. Hiding the
                    // label instead was worse again: a grey button with only a
                    // circle in it says less than the words do.
                    endIcon={
                      <CircularProgress
                        size={16}
                        color="inherit"
                        sx={{ visibility: runInFlight || savingBeforeRun ? "visible" : "hidden" }}
                      />
                    }
                  >
                    Execute Study
                  </Button>
                  {runInFlight && (
                    <Button
                      variant="outlined"
                      color="error"
                      size="medium"
                      sx={{ ml: 1.5 }}
                      disabled={!currentProject?.id || runStatus.cancelling}
                      onClick={handleCancelSimulation}
                      // Same treatment as the primary, and the label still
                      // changes here: this button's width moves nothing, it is
                      // last in the row, and "Cancelling…" is the one word the
                      // reader is waiting for.
                      endIcon={
                        <CircularProgress
                          size={16}
                          color="inherit"
                          sx={{ visibility: runStatus.cancelling ? "visible" : "hidden" }}
                        />
                      }
                    >
                      {runStatus.cancelling ? "Cancelling…" : "Cancel run"}
                    </Button>
                  )}
                </Box>
                {/* Read-only review of the inputs, opened on demand. */}
                <AssumptionCheck cfg={mergedCfg} isGreek={isGreek} />
              </Stack>
              {/* Cancellation is cooperative: the request sets a flag and the
                  engine stops when it next looks, which is on the clock while
                  it is optimising but only at the phase boundary while it is
                  building the timeseries or running the PV chain. A spinner
                  with nothing beside it reads as a stuck button, so the wait is
                  named instead of left to be guessed at. */}
              {runStatus.cancelling ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  {mapProgressMessage("Cancellation requested. The run stops at its next checkpoint, which can take a few seconds while a long step finishes.", isGreek)}
                </Typography>
              ) : runInFlight && cancelRequestedRef.current ? (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  {mapProgressMessage("The cancellation has been recorded and the run has not stopped yet. It will end at its next checkpoint.", isGreek)}
                </Typography>
              ) : null}
              {shadingPendingMessage ? (
                <Box ref={shadingNoticeRef} tabIndex={-1} sx={{ mt: 1.5, color: "text.secondary" }}>
                  <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                    {shadingCalc?.progress != null ? <CircularProgress size={14} color="inherit" /> : <InfoOutlinedIcon sx={{ fontSize: 16 }} />}
                    <Typography variant="caption" role="status">
                      {shadingCalc?.progress != null
                        ? (isGreek ? "Υπολογισμός σκιάσεων…" : "Calculating shading…")
                        : (isGreek ? "Οι σκιάσεις της διάταξης χρειάζονται ενημέρωση." : "Layout shading needs updating.")}
                    </Typography>
                  {shadingCalc?.progress == null &&
                    <Button size="small"
                      // An unvalidated layout cannot be calculated; that note is shown below.
                      disabled={sceneIssues.length > 1}
                      onClick={calculateSceneShading}>{isGreek ? "Υπολογισμός σκιάσεων" : "Calculate shading"}</Button>}
                  {shadingCalc?.progress != null && <Button color="inherit" size="small"
                    onClick={() => { shadingCancelRef.current = true; }}>{isGreek ? "Ακύρωση" : "Cancel"}</Button>}
                  </Stack>
                  <Box component="details" sx={{ mt: 0.5, typography: "caption" }}>
                    <Box component="summary" sx={{ cursor: "pointer" }}>{isGreek ? "Λεπτομέρειες και επιλογές" : "Details and options"}</Box>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {isGreek ? "Προσανατολισμοί προς ενημέρωση: " : "Orientations to update: "}{shadingPending.join(", ")}
                    </Typography>
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {isGreek
                        ? "Οι σκιάσεις υπολογίζονται με τον «Υπολογισμό σκιάσεων» της φυσικής διάταξης και αποθηκεύονται μαζί της. Μπορείτε να τις υπολογίσετε και εδώ και να αποθηκεύσετε το έργο. Χωρίς σκιάσεις διάταξης, παραμένει η τυπική σκίαση μεταξύ σειρών, αλλά παραλείπονται τα εμπόδια, οι άλλες πλευρές και η σκίαση ανά string."
                        : "Shading is calculated with “Calculate shading” in the physical layout and saved with it. You can also calculate it here and save the project. Without layout shading, standard row shading remains, but obstacle, other-surface and per-string shading are omitted."}
                    </Typography>
                    <Button color="inherit" size="small" disabled={!currentProject?.id || runInFlight || shadingCalc?.progress != null}
                      onClick={runWithoutLayoutShading}>{isGreek ? "Εκτέλεση χωρίς σκιάσεις διάταξης" : "Run without layout shading"}</Button>
                  </Box>
                  {shadingCalc?.progress != null && <LinearProgress variant="determinate" value={Math.round(shadingCalc.progress * 100)} sx={{ mt: 1 }} />}
                  {shadingCalc?.error && <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                    {isGreek ? translateLegacyText(shadingCalc.error) : shadingCalc.error}</Typography>}
                </Box>
              ) : null}
              {preRunBlockerList.length ? (
                <Alert severity="error" sx={{ mt: 2 }} data-testid="pre-run-blockers">
                  <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
                    {isGreek ? "Η μελέτη δεν μπορεί να ξεκινήσει:" : "The study cannot start:"}
                  </Typography>
                  <Stack spacing={1}>
                    {preRunBlockerList.map((item) => (
                      <Box key={item.id}>
                        <Typography variant="body2">{item.text}</Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                          <Button size="small" variant="outlined" color="inherit" onClick={() => navigate(item.fixPath)}>{item.fixLabel}</Button>
                          {item.secondary ? (
                            <Button size="small" color="inherit" onClick={() => navigate(item.secondary.path)}>{item.secondary.label}</Button>
                          ) : null}
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                </Alert>
              ) : null}
              {feasibilityBlockingErrorText ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {feasibilityBlockingErrorText}
                </Alert>
              ) : null}
              {/* Anchored to the row, not to the Execute button. At
                  right-start off the button it landed exactly where the Cancel
                  button appears when a run starts, covered it for four seconds
                  and then vanished to reveal it -- which read as the row
                  jumping. Off the row it clears both buttons whatever the row
                  is showing. keepMounted so the first open does not have to
                  wait for the anchor to be measured. */}
              <Popper
                open={triggerToastOpen}
                anchorEl={buttonRowRef.current}
                placement="right-start"
                transition
                keepMounted
                disablePortal={false}
              >
                {({ TransitionProps }) => (
                  <Grow {...TransitionProps}>
                    <Box sx={{ ml: 1.5 }}>
                      <ClickAwayListener onClickAway={() => setTriggerToastOpen(false)}>
                        <Alert
                          severity="success"
                          sx={{
                            boxShadow: "0 12px 30px rgba(15,23,42,0.18)",
                            borderRadius: 2,
                          }}
                        >
                          {mapProgressMessage("Simulation triggered successfully.", isGreek)}
                        </Alert>
                      </ClickAwayListener>
                    </Box>
                  </Grow>
                )}
              </Popper>
              {runStatus.error ? (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {mapSimulationError(runStatus.error, isGreek)}
                </Alert>
              ) : runStatus.status === "success" ? (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {mapProgressMessage("Simulation completed successfully.", isGreek)}
                </Alert>
              ) : null}
              {/* No "Simulation status: Running" line here any more. It said
                  exactly what the status card directly below says -- state,
                  phase and progress -- one card higher and in less detail.
                  Errors and the completion notice stay: they are outcomes,
                  and belong next to the button that produced them. */}
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "background.paper",
            }}
          >
            <Stack spacing={1.25}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                component="h2"
                sx={{ color: "text.primary", fontWeight: 700 }}
                  >
                    Simulation Status
                  </Typography>
                </Box>
                {runInFlight && <CircularProgress size={22} aria-label={isGreek ? "Η μελέτη εκτελείται" : "Study in progress"} />}
              </Box>
              {statusLoadError ? (
                <Alert severity="warning" sx={{ mb: 1.25 }}>
                  {isGreek
                    ? "Η κατάσταση της μελέτης δεν ήταν δυνατό να διαβαστεί από τον διακομιστή, οπότε τα παρακάτω δεν δείχνουν την τελευταία εκτέλεση. Αν μια μελέτη εκτελείται, συνεχίζει κανονικά. Ανανεώστε τη σελίδα σε λίγο."
                    : "The study status could not be read from the server, so the fields below do not show the latest run. A study that is running carries on. Refresh the page in a moment."}
                </Alert>
              ) : null}
              <Grid container spacing={1.25}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      borderLeft: "3px solid",
                      borderColor: "primary.main",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.75,
                      minWidth: 0,
                      backgroundColor: "action.hover",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "text.primary",
                        typography: "subtitle2",
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                      }}
                    >
                      Status
                    </Typography>
                    {statusLoading ? (
                      <Skeleton width={120} height={22} />
                    ) : (
                      <Typography
                        sx={{
                          fontWeight: 600,
                          typography: "body2",
                          color: statusTone(simulationStatus.status).color,
                        }}
                      >
                        {mapProgressMessage(statusTone(simulationStatus.status).label, isGreek)}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      borderLeft: "3px solid",
                      borderColor: "primary.main",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.75,
                      minWidth: 0,
                      backgroundColor: "action.hover",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "text.primary",
                        typography: "subtitle2",
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                      }}
                    >
                      <span title={isGreek
                        ? "Η πιο πρόσφατη προσπάθεια, ακόμη κι αν ακυρώθηκε ή απέτυχε."
                        : "The most recent attempt, including cancelled or failed runs."}>
                        {isGreek ? "Τελευταία εκτέλεση" : "Last run"}
                      </span>{` (${localTimeZone})`}
                    </Typography>
                    {statusLoading ? (
                      <Skeleton width={160} height={22} />
                    ) : (
                      <Typography sx={{ fontWeight: 600, typography: "body2" }}>
                        {formatTimestamp(simulationStatus.lastRun)}
                      </Typography>
                    )}
                  </Box>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <Box
                    sx={{
                      borderLeft: "3px solid",
                      borderColor: "primary.main",
                      borderRadius: 1,
                      px: 1.5,
                      py: 0.75,
                      minWidth: 0,
                      backgroundColor: "action.hover",
                    }}
                  >
                    <Typography
                      sx={{
                        color: "text.primary",
                        typography: "subtitle2",
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                      }}
                    >
                      Duration
                    </Typography>
                    {statusLoading ? (
                      <Skeleton width={80} height={22} />
                    ) : (
                      <Typography
                        sx={{
                          fontWeight: 600,
                          typography: "body2",
                          ...(runStatus.runTime ? null : placeholderSx),
                        }}
                      >
                        {runStatus.runTime ? `${runStatus.runTime}` : "\u2014"}
                      </Typography>
                    )}
                  </Box>
                </Grid>
              </Grid>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{ color: "text.primary", fontWeight: 700, minWidth: 90 }}
                  >
                    Progress:
                  </Typography>
                  <Box sx={{ flex: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={displayedProgress}
                      sx={{
                        height: 10,
                        borderRadius: 999,
                        backgroundColor: "rgba(15,23,42,0.08)",
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 999,
                          backgroundColor: statusTone(simulationStatus.status).color,
                        },
                      }}
                    />
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{
                      color: "text.primary",
                      fontWeight: 700,
                      minWidth: 48,
                      textAlign: "right",
                    }}
                  >
                    {displayedProgressLabel}
                  </Typography>
                </Box>
                <Box sx={{ mt: 1, display: "grid", gap: 0.5 }}>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.primary", fontWeight: 700, minWidth: 90 }}
                    >
                      Phase:
                    </Typography>
                    {statusLoading ? (
                      <Skeleton width={160} height={18} />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          ...(simulationStatus.phase ? null : placeholderSx),
                        }}
                      >
                        {simulationStatus.status === "Queued"
                          ? mapPhaseLabel("queued", isGreek)
                          : simulationStatus.phase ? mapPhaseLabel(simulationStatus.phase, isGreek) : "\u2014"}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "text.primary", fontWeight: 700, minWidth: 90 }}
                    >
                      Message:
                    </Typography>
                    {statusLoading ? (
                      <Skeleton width={220} height={18} />
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          ...(simulationStatus.message ? null : placeholderSx),
                        }}
                      >
                        {(simulationStatus.status === "Failed" ? mapSimulationError(simulationStatus.message, isGreek) : mapProgressMessage(simulationStatus.message, isGreek)) || "\u2014"}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Stack spacing={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 2,
                }}
              >
                <Box>
                  <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.5 }}>
                  <Typography
                    variant="subtitle1"
                component="h2"
                sx={{ color: "text.primary" }}
                  >
                    Reports
                  </Typography>
                  <ManualLink settlementType={cfg?.settlementType} section="reports" />
                  </Box>
                <Box>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    Download the proposal PDF or generate the optimization results report.
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 0.5 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Reports reflect the results of the most recent successful simulation run.
                    </Typography>
                  </Box>
                  {simulationStatus.reportsAvailable && (
                    <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                      {isGreek ? "Τελευταία επιτυχής εκτέλεση" : "Last successful run"}{` (${localTimeZone}): `}
                      {simulationStatus.lastCompletedAt
                        ? <time dateTime={simulationStatus.lastCompletedAt}>{formatTimestamp(simulationStatus.lastCompletedAt)}</time>
                        : "—"}
                    </Typography>
                  )}
                </Box>
                </Box>
                {runIsBtm && !runRegulatoryMode && currentProject?.id && simulationStatus.reportsAvailable && !demoExportDisabledMessage ? (
                  <Button variant="outlined" startIcon={<AssessmentOutlinedIcon />} onClick={() => setStudyResultsOpen(true)}
                    sx={{ flexShrink: 0, alignSelf: "flex-start" }}>
                    {isGreek ? "Αποτελέσματα μελέτης" : "Study results"}
                  </Button>
                ) : null}
              </Box>
              <StudyResultsDialog open={studyResultsOpen} onClose={() => setStudyResultsOpen(false)} isGreek={isGreek}
                projectId={currentProject?.id} terms={studyResultsTerms}
                stale={simulationStatus.reportsAvailable && reportConfigIsStale} staleText={isGreek ? translateLegacyText(STALE_CONFIG_WARNING) : STALE_CONFIG_WARNING}
                onDownloadProposal={handleDownloadCommercialOffer} onDownloadTechnical={handleDownloadProposal}
                proposalBusy={Boolean(reportStatus.commercialOfferDownloading)} technicalBusy={Boolean(reportStatus.proposalDownloading)}
                feedback={commercialOfferFeedback}
                indicativePrices={studyIndicativePrices}
                onFixPrices={() => { setStudyResultsOpen(false); navigate("/feasibility/economics#field-econ-simple-pv"); }} />
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: "repeat(2, minmax(0, 1fr))" }, gap: 1, "& > .MuiAlert-root": { gridColumn: "1 / -1" } }}>
                {(() => {
                  const reportsLocked =
                    !simulationStatus.reportsAvailable ||
                    Boolean(demoExportDisabledMessage);
                  const lockedTitle =
                    demoExportDisabledMessage ||
                    (isGreek
                      ? "Οι αναφορές θα είναι διαθέσιμες μετά την πρώτη επιτυχημένη εκτέλεση της μελέτης."
                      : "Reports will be available after the first successful simulation run.");
                  return (
                    <>
                {demoExportDisabledMessage ? (
                  <Alert severity="warning">{demoExportDisabledMessage}</Alert>
                ) : null}
                {/* No notice that the reports come from an earlier run: the caption
                    above already says they reflect the most recent successful
                    run, and during a run the notice read as a failure. */}
                {simulationStatus.reportsAvailable && reportConfigIsStale ? (
                  <Alert severity="warning">{STALE_CONFIG_WARNING}</Alert>
                ) : null}
                {runIsBtm || runRegulatoryMode ? (
                  <CommercialOfferCard key={currentProject?.id || "unsaved"} isGreek={isGreek}
                    regulatoryMode={runRegulatoryMode}
                    sx={{ ...reportCardSx, gridColumn: { lg: "1 / -1" } }} disabled={!currentProject?.id || reportsLocked}
                    lockedTitle={lockedTitle} busy={Boolean(reportStatus.commercialOfferDownloading)}
                    feedback={commercialOfferFeedback}
                    onEdit={() => navigate(`/feasibility/general#${COMMERCIAL_OFFER_SECTION_ID}`)}
                    onDownload={handleDownloadCommercialOffer} />
                ) : null}
                {runIsBtm && assetTypeDefinition(cfg.systemType)?.hasBess ? (
                  <BatteryComparisonCard key={`cmp-${currentProject?.id || "unsaved"}`} isGreek={isGreek}
                    projectId={currentProject?.id} config={finalPayloadConfig}
                    commitConfig={(update) => history.commit(update(mergedCfg))}
                    disabled={!currentProject?.id || reportsLocked} lockedTitle={lockedTitle}
                    stale={simulationStatus.reportsAvailable && reportConfigIsStale} studyRunning={runInFlight}
                    studyCompletedAt={simulationStatus.lastCompletedAt}
                    onOpenBess={() => navigate("/project/asset?tab=bess")}
                    sx={{ ...reportCardSx, gridColumn: { lg: "1 / -1" } }} />
                ) : null}
                <Box
                  sx={{ ...reportCardSx, alignSelf: "start" }}
                >
                  <Box>
                    <Typography variant="subtitle2" component="h3">Technical proposal (PDF)</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Download the proposal document.
                    </Typography>
                  </Box>
                  <Box
                    title={reportsLocked ? lockedTitle : undefined}
                    sx={{
                      display: "inline-flex",
                        flexWrap: "wrap",
                      cursor: reportsLocked ? "not-allowed" : "pointer",
                    }}
                  >
                    <Button
                      variant="contained"
                      onClick={handleDownloadProposal}
                      disabled={
                        !currentProject?.id ||
                        reportsLocked ||
                        reportStatus.proposalDownloading
                      }
                      sx={{
                        position: "relative",
                        pointerEvents: reportsLocked ? "none" : "auto",
                      }}
                    >
                      {reportStatus.proposalDownloading ? (
                        <CircularProgress
                          size={22}
                          color="inherit"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            margin: "auto",
                          }}
                        />
                      ) : null}
                      <Box
                        component="span"
                        sx={{
                          visibility: reportStatus.proposalDownloading
                            ? "hidden"
                            : "visible",
                        }}
                      >
                        Download
                      </Box>
                    </Button>
                  </Box>
                </Box>
                {/* Virtual net billing: the teasers are not generated for it yet. */}
                {!runIsVnb && (<>
                <Box
                  sx={reportCardSx}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" component="h3">Web Teaser (HTML)</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Download or publish the shareable one-page investor teaser.
                        Revoking disables both public links (one-pager and detailed).
                      </Typography>
                    </Box>
                    <Box
                      title={reportsLocked ? lockedTitle : undefined}
                      sx={{
                        display: "inline-flex",
                        flexWrap: "wrap",
                        gap: 1,
                        cursor: reportsLocked ? "not-allowed" : "pointer",
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={handleDownloadTeaser}
                        disabled={
                          !currentProject?.id ||
                          reportsLocked ||
                          reportStatus.teaserDownloading
                        }
                        sx={{
                              position: "relative",
                            pointerEvents: reportsLocked ? "none" : "auto",
                        }}
                      >
                        {reportStatus.teaserDownloading ? (
                          <CircularProgress
                            size={22}
                            color="inherit"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              margin: "auto",
                            }}
                          />
                        ) : null}
                        <Box
                          component="span"
                          sx={{
                            visibility: reportStatus.teaserDownloading
                              ? "hidden"
                              : "visible",
                          }}
                        >
                          Download
                        </Box>
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handlePublishTeaser}
                        disabled={
                          !currentProject?.id ||
                          reportsLocked ||
                          teaserPublish.loading
                        }
                        sx={{
                            position: "relative",
                          pointerEvents: reportsLocked ? "none" : "auto",
                        }}
                      >
                        {teaserPublish.loading ? (
                          <CircularProgress
                            size={22}
                            color="inherit"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              margin: "auto",
                            }}
                          />
                        ) : null}
                        <Box
                          component="span"
                          sx={{
                            visibility: teaserPublish.loading ? "hidden" : "visible",
                          }}
                        >
                          Publish
                        </Box>
                      </Button>
                      <Button
                        variant="text"
                        color="error"
                        onClick={handleRevokeTeaser}
                        disabled={
                          !currentProject?.id ||
                          reportsLocked ||
                          teaserPublish.revoking
                        }
                        sx={{ fontWeight: 600 }}
                      >
                        {teaserPublish.revoking ? "Revoking..." : "Revoke links"}
                      </Button>
                    </Box>
                  </Box>
                  {(teaserPublish.url || teaserPublish.error || teaserPublish.revoked) ? (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        wordBreak: "break-all",
                        color: teaserPublish.error ? "error.main" : "text.secondary",
                      }}
                    >
                      {teaserPublish.error
                        ? `Error: ${teaserPublish.error}`
                        : teaserPublish.revoked && !teaserPublish.url
                        ? "Public link revoked. Publish again to create a new link."
                        : `Public link: ${teaserPublish.url}${
                            teaserPublish.copied ? " (copied to clipboard)" : ""
                          } · Views: ${teaserPublish.views}${
                            teaserPublish.expiresAt
                              ? ` · Expires: ${new Date(teaserPublish.expiresAt).toLocaleDateString("en-GB")}`
                              : ""
                          }`}
                    </Typography>
                  ) : null}
                </Box>
                <Box
                  sx={reportCardSx}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      gap: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="subtitle2" component="h3">Detailed Teaser (interactive)</Typography>
                      <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        Charts, downside analysis and dispatch evidence on one
                        bilingual (EL/EN) page. Forward price levels are shown as a
                        shape-only index — absolute values stay in the report.
                      </Typography>
                    </Box>
                    <Box
                      title={reportsLocked ? lockedTitle : undefined}
                      sx={{
                        display: "inline-flex",
                        flexWrap: "wrap",
                        gap: 1,
                        cursor: reportsLocked ? "not-allowed" : "pointer",
                      }}
                    >
                      <Button
                        variant="outlined"
                        onClick={handleDownloadDetailedTeaser}
                        disabled={
                          !currentProject?.id ||
                          reportsLocked ||
                          reportStatus.detailedTeaserDownloading
                        }
                        sx={{
                              position: "relative",
                            pointerEvents: reportsLocked ? "none" : "auto",
                        }}
                      >
                        {reportStatus.detailedTeaserDownloading ? (
                          <CircularProgress
                            size={22}
                            color="inherit"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              margin: "auto",
                            }}
                          />
                        ) : null}
                        <Box
                          component="span"
                          sx={{
                            visibility: reportStatus.detailedTeaserDownloading
                              ? "hidden"
                              : "visible",
                          }}
                        >
                          Download
                        </Box>
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handlePublishDetailedTeaser}
                        disabled={
                          !currentProject?.id ||
                          reportsLocked ||
                          detailedTeaserPublish.loading
                        }
                        sx={{
                            position: "relative",
                          pointerEvents: reportsLocked ? "none" : "auto",
                        }}
                      >
                        {detailedTeaserPublish.loading ? (
                          <CircularProgress
                            size={22}
                            color="inherit"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              margin: "auto",
                            }}
                          />
                        ) : null}
                        <Box
                          component="span"
                          sx={{
                            visibility: detailedTeaserPublish.loading
                              ? "hidden"
                              : "visible",
                          }}
                        >
                          Publish
                        </Box>
                      </Button>
                    </Box>
                  </Box>
                  {(detailedTeaserPublish.url ||
                    detailedTeaserPublish.error ||
                    detailedTeaserPublish.revoked) ? (
                    <Typography
                      variant="body2"
                      sx={{
                        mt: 1,
                        wordBreak: "break-all",
                        color: detailedTeaserPublish.error ? "error.main" : "text.secondary",
                      }}
                    >
                      {detailedTeaserPublish.error
                        ? `Error: ${detailedTeaserPublish.error}`
                        : detailedTeaserPublish.revoked && !detailedTeaserPublish.url
                        ? "Public link revoked. Publish again to create a new link."
                        : `Public link: ${detailedTeaserPublish.url}${
                            detailedTeaserPublish.copied ? " (copied to clipboard)" : ""
                          } · Views: ${detailedTeaserPublish.views}${
                            detailedTeaserPublish.expiresAt
                              ? ` · Expires: ${new Date(detailedTeaserPublish.expiresAt).toLocaleDateString("en-GB")}`
                              : ""
                          }`}
                    </Typography>
                  ) : null}
                </Box>
                </>)}
                <Box
                  sx={reportCardSx}
                >
                  <Box>
                    <Typography variant="subtitle2" component="h3">Optimization Results (XLSX)</Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Generate and download the results report.
                    </Typography>
                  </Box>
                  {reportStatus.generated ? (
                    <Box
                      title={reportsLocked ? lockedTitle : undefined}
                      sx={{
                        display: "inline-flex",
                        flexWrap: "wrap",
                        cursor: reportsLocked ? "not-allowed" : "pointer",
                      }}
                    >
                    <Button
                      variant="outlined"
                      onClick={handleDownloadReport}
                      disabled={reportsLocked || reportStatus.reportDownloading}
                      sx={{
                        position: "relative",
                        pointerEvents: reportsLocked ? "none" : "auto",
                      }}
                    >
                      {reportStatus.reportDownloading ? (
                        <CircularProgress
                          size={22}
                          color="inherit"
                          sx={{
                            position: "absolute",
                            inset: 0,
                            margin: "auto",
                          }}
                        />
                      ) : null}
                      <Box
                        component="span"
                        sx={{
                          visibility: reportStatus.reportDownloading ? "hidden" : "visible",
                        }}
                      >
                        Download
                      </Box>
                      </Button>
                    </Box>
                  ) : (
                    <Box
                      title={reportsLocked ? lockedTitle : undefined}
                      sx={{
                        display: "inline-flex",
                        flexWrap: "wrap",
                        cursor: reportsLocked ? "not-allowed" : "pointer",
                      }}
                    >
                      <Button
                        variant="contained"
                        onClick={handleGenerateReport}
                        disabled={!currentProject?.id || reportStatus.generating || reportsLocked}
                        sx={{
                          pointerEvents: reportsLocked ? "none" : "auto",
                          position: "relative",
                          minWidth: 120,
                        }}
                      >
                        {reportStatus.generating ? (
                          <CircularProgress
                            size={22}
                            color="inherit"
                            sx={{
                              position: "absolute",
                              inset: 0,
                              margin: "auto",
                            }}
                          />
                        ) : null}
                        <Box
                          component="span"
                          sx={{
                            visibility: reportStatus.generating ? "hidden" : "visible",
                          }}
                        >
                          Generate
                        </Box>
                      </Button>
                    </Box>
                  )}
                </Box>
                    </>
                  );
                })()}
                {reportStatus.error && <Alert severity="error">{mapSimulationError(reportStatus.error, isGreek)}</Alert>}
                {reportStatus.success && <Alert severity="success">{mapProgressMessage(reportStatus.success, isGreek)}</Alert>}
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
      <Dialog open={preRunWarningsOpen} onClose={() => setPreRunWarningsOpen(false)} maxWidth="sm" fullWidth
        aria-labelledby="pre-run-warnings-title">
        <DialogTitle id="pre-run-warnings-title">{isGreek ? "Ελέγξτε πριν από την εκτέλεση" : "Check before running"}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={1.5}>
            {preRunWarningList.map((item) => (
              <Alert key={item.id} severity="warning"
                action={<Button color="inherit" size="small" onClick={() => { setPreRunWarningsOpen(false); navigate(item.fixPath); }}>
                  {isGreek ? "Διόρθωση" : "Fix"}</Button>}>
                {item.text}
              </Alert>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPreRunWarningsOpen(false)}>{isGreek ? "Ακύρωση" : "Cancel"}</Button>
          <Button variant="contained" onClick={() => {
            acceptPreRunWarnings(warningsKey(preRunWarningList));
            setPreRunWarningsOpen(false);
            handleExecuteClick();
          }}>
            {isGreek ? "Εκτέλεση παρ' όλα αυτά" : "Run anyway"}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={unsavedDialogOpen}
        onClose={() => setUnsavedDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <SvgIcon sx={{ color: "#f59e0b", fontSize: 28 }}>
            <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
          </SvgIcon>
          Unsaved Changes
        </DialogTitle>
        <DialogContent
          sx={{
            borderLeft: "4px solid #f59e0b",
            backgroundColor: "rgba(245,158,11,0.08)",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {unsavedSaveError
              ? (isGreek
                  ? `Η μελέτη δεν ξεκίνησε, γιατί το έργο δεν αποθηκεύτηκε: ${unsavedSaveError}`
                  : `The study did not start because the project could not be saved: ${unsavedSaveError}`)
              : (isGreek
                  ? "Το έργο δεν έχει αποθηκευτεί ακόμη. Αποθηκεύστε το με «Αρχείο → Αποθήκευση ως…» και πατήστε ξανά «Εκτέλεση μελέτης»."
                  : "The project has not been saved yet. Save it with File → Save as… and press Execute Study again.")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setUnsavedDialogOpen(false)} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </FeasibilityShell>
  );
}


